import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { PageHeader } from '../common/PageHeader';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../ui/dialog';
import { settingsApi, systemApi, fontApi, designApi, deploymentApi, loginPageApi, templateApi } from '../../lib/api';
import api from '../../lib/api';
import { legalApi, dropboxApi } from '../../lib/api';
import { EmailEditor } from '../common/EmailEditor';
import { UserManagementCard } from '../common/UserManagementCard';
import { AutoBackupCard } from '../common/AutoBackupCard';
import { AdminPermissionsCard } from '../common/AdminPermissionsCard';
import { CancellationRulesCard, CancellationReasonsCard } from '../settings/CancellationSettings';
import ExpenseCategoriesSettings from '../settings/ExpenseCategoriesSettings';
import { PublicHeaderImageCard } from '../common/PublicHeaderImageCard';
import { applyColors } from '../../hooks/useDesignColors';
import { toast } from 'sonner';
import { downloadBlob } from '../../lib/utils';
import { 
    Loader2, Mail, Server, Save, Code, RefreshCw, Database, 
    CheckCircle, AlertTriangle, HardDrive, Download, Upload, Package, 
    FileUp, ArrowRight, XCircle, Palette, Trash2, Type, Search, Eye, RotateCcw, Archive, Send, ChevronUp, ChevronDown, Award, Pencil, Globe, GraduationCap, FileText, FolderOpen, Plus, Receipt, Shield, Clock, Copy, Wrench, Ban, Activity, Cpu, MemoryStick, Image
} from 'lucide-react';

const BACKEND_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "");

// Default colors
const DEFAULT_COLORS = {
    primary: '#CC0000',
    primary_hover: '#990000',
    secondary: '#1F2937',
    sidebar_bg: '#1F2937',
    sidebar_text: '#9CA3AF',
    sidebar_active: '#CC0000',
    header_bg: '#FFFFFF',
    body_bg: '#F3F4F6',
    card_bg: '#FFFFFF',
    text_primary: '#111827',
    text_secondary: '#6B7280',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
    btn_primary: '#CC0000',
    btn_edit: '#1F2937',
    btn_renew: '#2563EB',
    btn_danger: '#DC2626',
    btn_primary_text: '#FFFFFF',
    btn_edit_text: '#FFFFFF',
    btn_renew_text: '#FFFFFF',
    btn_danger_text: '#FFFFFF',
    // Public-Header (öffentliche Seminarseite)
    public_header_overlay_color: '#1F2937',
    public_header_overlay_opacity: 60,
    public_header_parallax: false,
    public_header_image_enabled: false,
    public_header_extend_topbar: false,
    public_header_title: 'Fort- & Weiterbildungen',
    public_header_subtitle: 'Entdecken Sie unsere Seminare der BGPR-Akademie sowie Angebote externer Partner für Herzgruppenleiter.',
};

// BGPR Corporate Identity colors (based on bgpr.de)
const BGPR_CI_COLORS = {
    primary: '#0092B5',
    primary_hover: '#007A99',
    secondary: '#2B5B6C',
    sidebar_bg: '#2B5B6C',
    sidebar_text: '#A0BEC8',
    sidebar_active: '#E5007D',
    header_bg: '#FFFFFF',
    body_bg: '#F5F8FA',
    card_bg: '#FFFFFF',
    text_primary: '#1A2E35',
    text_secondary: '#5A7D8A',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#0092B5',
    btn_primary: '#E5007D',
    btn_edit: '#2B5B6C',
    btn_renew: '#0092B5',
    btn_danger: '#DC2626',
    btn_primary_text: '#FFFFFF',
    btn_edit_text: '#FFFFFF',
    btn_renew_text: '#FFFFFF',
    btn_danger_text: '#FFFFFF',
    public_header_overlay_color: '#2B5B6C',
    public_header_overlay_opacity: 60,
    public_header_parallax: false,
    public_header_image_enabled: false,
    public_header_extend_topbar: false,
    public_header_title: 'Fort- & Weiterbildungen',
    public_header_subtitle: 'Entdecken Sie unsere Seminare der BGPR-Akademie sowie Angebote externer Partner für Herzgruppenleiter.',
};

const COLOR_PRESETS = [
    { id: 'standard', name: 'Standard (Rot)', colors: DEFAULT_COLORS, accent: '#CC0000', secondary: '#1F2937' },
    { id: 'bgpr-ci', name: 'BGPR CI (Teal/Magenta)', colors: BGPR_CI_COLORS, accent: '#0092B5', secondary: '#E5007D' },
];

// Color groups for UI
const COLOR_GROUPS = {
    'Hauptfarben': {
        primary: { label: 'Primärfarbe (Logo/Buttons)', desc: 'Hauptfarbe der App' },
        primary_hover: { label: 'Primärfarbe Hover', desc: 'Beim Überfahren mit der Maus' },
        secondary: { label: 'Sekundärfarbe', desc: 'Dunkle Akzentfarbe' },
    },
    'Sidebar': {
        sidebar_bg: { label: 'Hintergrund', desc: 'Sidebar-Hintergrundfarbe' },
        sidebar_text: { label: 'Text', desc: 'Menüpunkte (inaktiv)' },
        sidebar_active: { label: 'Aktiver Menüpunkt', desc: 'Hintergrund des aktiven Menüs' },
    },
    'Seitenbereiche': {
        header_bg: { label: 'Header-Hintergrund', desc: 'Obere Leiste' },
        body_bg: { label: 'Seiten-Hintergrund', desc: 'Hauptbereich der Seite' },
        card_bg: { label: 'Karten-Hintergrund', desc: 'Weiße Boxen/Karten' },
    },
    'Text': {
        text_primary: { label: 'Haupttext', desc: 'Überschriften und wichtiger Text' },
        text_secondary: { label: 'Sekundärtext', desc: 'Beschreibungen und Hinweise' },
    },
    'Status-Farben': {
        success: { label: 'Erfolg', desc: 'Bestätigungen, Aktive Status' },
        warning: { label: 'Warnung', desc: 'Hinweise, Ablaufende Lizenzen' },
        error: { label: 'Fehler', desc: 'Fehlermeldungen, Abgelaufene Lizenzen' },
        info: { label: 'Info', desc: 'Informationen, Links' },
    },
    'Button-Farben (Hintergrund)': {
        btn_primary: { label: 'Primär-Button', desc: 'Hauptaktionen (Erstellen, Speichern)' },
        btn_edit: { label: 'Bearbeiten-Button', desc: 'Bearbeiten-Aktionen' },
        btn_renew: { label: 'Verlängern-Button', desc: 'Lizenzverlängerung' },
        btn_danger: { label: 'Gefahr-Button', desc: 'Löschen, Deaktivieren' },
    },
    'Button-Farben (Schrift)': {
        btn_primary_text: { label: 'Primär-Button Schrift', desc: 'Textfarbe für Hauptaktionen' },
        btn_edit_text: { label: 'Bearbeiten-Button Schrift', desc: 'Textfarbe für Bearbeiten' },
        btn_renew_text: { label: 'Verlängern-Button Schrift', desc: 'Textfarbe für Verlängerung' },
        btn_danger_text: { label: 'Gefahr-Button Schrift', desc: 'Textfarbe für Löschen, Deaktivieren' },
    },
};

// Available fonts
const AVAILABLE_FONTS = [
    'Titillium Web',
    'Open Sans', 
    'Roboto',
    'System Standard',
];

// Robots options
const ROBOTS_OPTIONS = [
    { value: 'noindex, nofollow', label: 'Nicht indexieren, Links nicht folgen (Empfohlen)' },
    { value: 'noindex, follow', label: 'Nicht indexieren, aber Links folgen' },
    { value: 'index, nofollow', label: 'Indexieren, aber Links nicht folgen' },
    { value: 'index, follow', label: 'Vollständig indexieren (Öffentliche Seite)' },
];

export function SettingsPage() {
    const { tab } = useParams();
    const activeTab = tab || 'php-version';
    const [smtpSettings, setSmtpSettings] = useState({
        host: '',
        port: 587,
        username: '',
        password: '',
        from_email: '',
        from_name: 'BGPR Verwaltung',
        reply_to_email: '',
        reply_to_name: '',
        use_tls: true,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [systemStatus, setSystemStatus] = useState(null);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    
    // Design state
    const [designTab, setDesignTab] = useState('colors');
    const [colors, setColors] = useState(DEFAULT_COLORS);
    const [meta, setMeta] = useState({
        font_family: 'Titillium Web',
        meta_title: 'BGPR Verwaltung',
        meta_description: 'Verwaltungssystem für Herzsportgruppen',
        meta_keywords: '',
        meta_robots: 'noindex, nofollow',
        custom_css: '',
    });
    const [savingDesign, setSavingDesign] = useState(false);
    const [customPresets, setCustomPresets] = useState([]);
    const [renamingPresetId, setRenamingPresetId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [activePresetId, setActivePresetId] = useState(null);
    const presetImportInputRef = useRef(null);
    
    // Fonts state
    const [fonts, setFonts] = useState([]);
    const [fontFamilies, setFontFamilies] = useState([]);
    const [uploadingFont, setUploadingFont] = useState(false);
    const fontInputRef = useRef(null);
    
    // Version state
    const [appVersion, setAppVersion] = useState('1.0.0');
    const [versionBuild, setVersionBuild] = useState('');
    const [savingVersion, setSavingVersion] = useState(false);

    // Deployment-Readiness state
    const [readiness, setReadiness] = useState(null);
    const [readinessLoading, setReadinessLoading] = useState(false);
    const [smtpTestTo, setSmtpTestTo] = useState('');
    const [smtpTestSending, setSmtpTestSending] = useState(false);
    const [cleanupRunning, setCleanupRunning] = useState(false);
    const [backupDownloading, setBackupDownloading] = useState(false);
    const [maintenanceToggling, setMaintenanceToggling] = useState(false);

    useEffect(() => {
        loadSettings();
        checkSystemStatus();
        loadDesignSettings();
        loadFonts();
        loadVersion();
        loadCustomPresets();
        loadReadiness();
    }, []);

    const loadReadiness = async () => {
        setReadinessLoading(true);
        try {
            const res = await deploymentApi.readiness();
            setReadiness(res.data);
            if (res.data?.smtp_from && !smtpTestTo) {
                setSmtpTestTo(res.data.smtp_from);
            }
        } catch (e) {
            console.error('readiness load failed', e);
        } finally {
            setReadinessLoading(false);
        }
    };

    const handleCleanupTestData = async () => {
        const testCount = (readiness?.test_data?.users?.length || 0) + (readiness?.test_data?.instructors?.length || 0);
        if (!testCount) {
            toast.info('Keine Test-Daten erkannt — nichts zu tun.');
            return;
        }
        if (!window.confirm(`Wirklich alle ${testCount} erkannten Test-/Demo-Datensätze inkl. zugehöriger Buchungen, Lizenzen und Rechnungen löschen?\n\nVorher solltest Du ein Backup gezogen haben!`)) return;
        setCleanupRunning(true);
        try {
            const res = await deploymentApi.cleanupTestData();
            toast.success(res.data.message || 'Test-Daten entfernt');
            loadReadiness();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Cleanup fehlgeschlagen');
        } finally {
            setCleanupRunning(false);
        }
    };

    const handleCleanupOrphans = async () => {
        if (!window.confirm('Verwaiste Lizenzen, Verlängerungsanträge, Buchungen und Rechnungen entfernen, deren Akademie-Mitglied bereits gelöscht wurde?\n\nDiese Datensätze werden niemandem mehr angezeigt und sollten daher entfernt werden, um die DB sauber zu halten. Vorher Backup ziehen!')) return;
        setCleanupRunning(true);
        try {
            const res = await deploymentApi.cleanupOrphans();
            const d = res.data.deleted || {};
            const summary = Object.entries(d).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(', ') || 'keine';
            toast.success(`Verwaiste Daten bereinigt: ${summary}`);
            loadReadiness();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Cleanup fehlgeschlagen');
        } finally {
            setCleanupRunning(false);
        }
    };

    const [resetBookkeepingConfirm, setResetBookkeepingConfirm] = useState('');
    const [resetBookkeepingRunning, setResetBookkeepingRunning] = useState(false);

    const handleResetBookkeeping = async () => {
        if (resetBookkeepingConfirm !== 'BUCHHALTUNG-ZURUECKSETZEN') {
            toast.error('Bitte exakt „BUCHHALTUNG-ZURUECKSETZEN" eingeben, um zu bestätigen.');
            return;
        }
        if (!window.confirm('Wirklich ALLE Buchungen, Rechnungen, Stornierungen und Zahlungs-Belege löschen? Diese Aktion ist nicht rückgängig zu machen. Vorher Backup ziehen!')) return;
        setResetBookkeepingRunning(true);
        try {
            const res = await systemApi.resetBookkeeping(resetBookkeepingConfirm);
            const d = res.data?.deleted || {};
            const summary = Object.entries(d).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(', ') || 'keine Datensätze';
            toast.success(`Buchhaltung zurückgesetzt: ${summary}`);
            setResetBookkeepingConfirm('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reset fehlgeschlagen');
        } finally {
            setResetBookkeepingRunning(false);
        }
    };

    const handleSendSmtpTest = async () => {
        if (!smtpTestTo.trim()) {
            toast.error('Bitte Empfänger-E-Mail eingeben');
            return;
        }
        setSmtpTestSending(true);
        try {
            await deploymentApi.testSmtp(smtpTestTo.trim());
            toast.success(`Test-Mail an ${smtpTestTo.trim()} gesendet — bitte Postfach prüfen.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'SMTP-Test fehlgeschlagen');
        } finally {
            setSmtpTestSending(false);
        }
    };

    const handleBackupNow = async () => {
        setBackupDownloading(true);
        try {
            const res = await deploymentApi.backupNow();
            const blob = new Blob([res.data], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `bgpr_backup_${new Date().toISOString().slice(0,10)}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Backup heruntergeladen');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Backup fehlgeschlagen');
        } finally {
            setBackupDownloading(false);
        }
    };

    const handleToggleMaintenance = async (on) => {
        setMaintenanceToggling(true);
        try {
            await deploymentApi.setMaintenance({ enabled: on });
            toast.success(on ? 'Wartungsmodus AKTIV — öffentliche Seite zeigt Wartungshinweis.' : 'Wartungsmodus AUS — Seite ist öffentlich erreichbar.');
            loadReadiness();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Umschalten fehlgeschlagen');
        } finally {
            setMaintenanceToggling(false);
        }
    };


    const loadCustomPresets = async () => {
        try {
            const r = await api.get('/settings/color-presets');
            setCustomPresets(r.data || []);
        } catch { setCustomPresets([]); }
    };

    const handleDuplicatePreset = async (sourcePreset) => {
        try {
            const baseName = sourcePreset.name.replace(/\s*\(Kopie( \d+)?\)\s*$/, '');
            const existing = customPresets.map(p => p.name);
            let newName = `${baseName} (Kopie)`;
            let i = 2;
            while (existing.includes(newName)) {
                newName = `${baseName} (Kopie ${i++})`;
            }
            const r = await api.post('/settings/color-presets', {
                name: newName,
                colors: sourcePreset.colors,
            });
            const created = r.data;
            setCustomPresets(prev => [...prev, created]);
            setColors({ ...created.colors });
            setActivePresetId(created.id);
            toast.success(`Vorlage „${newName}" angelegt`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Duplizieren fehlgeschlagen');
        }
    };

    const handleExportPreset = (preset) => {
        const payload = {
            schema: 'bgpr-color-preset/v1',
            name: preset.name,
            colors: preset.colors,
            exported_at: new Date().toISOString(),
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const safeName = preset.name.replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '');
        a.href = url;
        a.download = `bgpr-farbvorlage-${safeName || 'preset'}.json`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`„${preset.name}" exportiert`);
    };

    const handleImportPresetClick = () => {
        presetImportInputRef.current?.click();
    };

    const handleImportPresetFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        try {
            const txt = await file.text();
            const parsed = JSON.parse(txt);
            const incomingColors = parsed?.colors;
            if (!incomingColors || typeof incomingColors !== 'object') {
                toast.error('Ungültige Datei: Feld „colors" fehlt');
                return;
            }
            const incomingName = (parsed.name || file.name.replace(/\.json$/i, '') || 'Importierte Vorlage').toString().slice(0, 80);
            // Sanitize: nur Schlüssel aus DEFAULT_COLORS übernehmen; Typ des Defaults respektieren
            const cleanColors = {};
            Object.keys(DEFAULT_COLORS).forEach(k => {
                const def = DEFAULT_COLORS[k];
                const v = incomingColors[k];
                if (typeof def === 'string') {
                    cleanColors[k] = typeof v === 'string' ? v : def;
                } else if (typeof def === 'boolean') {
                    cleanColors[k] = typeof v === 'boolean' ? v : def;
                } else if (typeof def === 'number') {
                    cleanColors[k] = typeof v === 'number' ? v : def;
                } else {
                    cleanColors[k] = v ?? def;
                }
            });
            // Kollisionsfreier Name
            const existing = customPresets.map(p => p.name);
            let finalName = incomingName;
            let n = 2;
            while (existing.includes(finalName)) {
                finalName = `${incomingName} (${n++})`;
            }
            const r = await api.post('/settings/color-presets', { name: finalName, colors: cleanColors });
            setCustomPresets(prev => [...prev, r.data]);
            setColors({ ...r.data.colors });
            setActivePresetId(r.data.id);
            toast.success(`„${finalName}" importiert`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Datei konnte nicht gelesen werden');
        }
    };

    const handleRenamePreset = async (presetId) => {
        const trimmed = (renameValue || '').trim();
        if (!trimmed) { setRenamingPresetId(null); return; }
        try {
            const r = await api.put(`/settings/color-presets/${presetId}`, { name: trimmed });
            setCustomPresets(prev => prev.map(p => p.id === presetId ? r.data : p));
            toast.success('Umbenannt');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Umbenennen fehlgeschlagen');
        } finally {
            setRenamingPresetId(null);
        }
    };

    const handleSavePresetColors = async (presetId) => {
        try {
            const r = await api.put(`/settings/color-presets/${presetId}`, { colors });
            setCustomPresets(prev => prev.map(p => p.id === presetId ? r.data : p));
            toast.success('Farben in Vorlage gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        }
    };

    const handleDeletePreset = async (presetId, name) => {
        if (!window.confirm(`Vorlage „${name}" wirklich löschen?`)) return;
        try {
            await api.delete(`/settings/color-presets/${presetId}`);
            setCustomPresets(prev => prev.filter(p => p.id !== presetId));
            toast.success('Vorlage gelöscht');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
        }
    };

    const loadVersion = async () => {
        try {
            const response = await api.get('/version');
            if (response.data) {
                setAppVersion(response.data.version || '1.0.0');
                setVersionBuild(response.data.build || '');
            }
        } catch (error) {
            console.log('Could not load version');
        }
    };

    const handleSaveVersion = async () => {
        if (!/^\d+\.\d+\.\d+$/.test(appVersion)) {
            toast.error('Version muss Format X.Y.Z haben (z.B. 1.1.0)');
            return;
        }
        
        setSavingVersion(true);
        try {
            const response = await api.post(`/version?version=${encodeURIComponent(appVersion)}`);
            toast.success(response.data.message || 'Version gespeichert!');
            setVersionBuild(response.data.build || '');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSavingVersion(false);
        }
    };
    
    const incrementVersion = (type) => {
        const parts = appVersion.split('.').map(Number);
        if (type === 'major') {
            parts[0]++;
            parts[1] = 0;
            parts[2] = 0;
        } else if (type === 'minor') {
            parts[1]++;
            parts[2] = 0;
        } else {
            parts[2]++;
        }
        setAppVersion(parts.join('.'));
    };

    const loadSettings = async () => {
        try {
            const response = await settingsApi.getSmtp();
            if (response.data && Object.keys(response.data).length > 0) {
                setSmtpSettings((prev) => ({ ...prev, ...response.data }));
            }
        } catch (error) {
            // Settings may not exist yet
        } finally {
            setLoading(false);
        }
    };
    
    const loadDesignSettings = async () => {
        try {
            const response = await designApi.get();
            if (response.data) {
                if (response.data.colors) {
                    const incoming = response.data.colors;
                    setColors(prev => ({ ...prev, ...incoming }));
                    // Wenn die geladenen Farben mit einer eingebauten Vorlage exakt übereinstimmen,
                    // diese als aktiv markieren – bei Custom-Vorlagen entscheidet erst der Klick.
                    const match = COLOR_PRESETS.find(p =>
                        p.colors.primary === incoming.primary &&
                        p.colors.sidebar_bg === incoming.sidebar_bg &&
                        p.colors.btn_primary === incoming.btn_primary
                    );
                    setActivePresetId(match ? match.id : null);
                }
                if (response.data.meta) {
                    setMeta(prev => ({ ...prev, ...response.data.meta }));
                }
            }
        } catch (error) {
            console.log('Using default design settings');
        }
    };
    
    const loadFonts = async () => {
        try {
            const response = await fontApi.getAll();
            setFonts(response.data.fonts || []);
            setFontFamilies(response.data.families || []);
        } catch (error) {
            console.log('Could not load fonts');
        }
    };

    const checkSystemStatus = async () => {
        try {
            const response = await systemApi.getStatus();
            setSystemStatus(response.data);
        } catch (error) {
            setSystemStatus({
                version: '1.0.0',
                database: 'connected',
                lastBackup: null,
                disk_usage: 'Nicht verfügbar',
            });
        }
    };

    const handleSmtpChange = (e) => {
        const value = e.target.type === 'number' ? parseInt(e.target.value) : e.target.value;
        setSmtpSettings({ ...smtpSettings, [e.target.name]: value });
    };

    const handleSaveSmtp = async () => {
        setSaving(true);
        try {
            await settingsApi.saveSmtp(smtpSettings);
            toast.success('SMTP-Einstellungen gespeichert');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleSystemUpdate = async () => {
        setUpdating(true);
        toast.info('Update wird gestartet...');
        
        try {
            const response = await systemApi.triggerUpdate();
            toast.success(response.data.message || 'System ist auf dem neuesten Stand');
        } catch (error) {
            toast.error('Update fehlgeschlagen');
        } finally {
            setUpdating(false);
        }
    };

    const handleBackup = async () => {
        toast.info('Backup wird erstellt...');
        try {
            const response = await systemApi.createBackup();
            const filename = `bgpr_backup_${new Date().toISOString().slice(0,10)}.json`;
            downloadBlob(response.data, filename);
            toast.success('Backup erfolgreich erstellt und heruntergeladen');
            setSystemStatus(prev => ({
                ...prev,
                lastBackup: new Date().toLocaleString('de-DE')
            }));
        } catch (error) {
            toast.error('Backup fehlgeschlagen');
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.name.endsWith('.json')) {
                toast.error('Bitte wählen Sie eine JSON-Backup-Datei');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleRestore = async () => {
        if (!selectedFile) {
            toast.error('Bitte wählen Sie eine Backup-Datei');
            return;
        }
        
        setRestoring(true);
        try {
            await systemApi.restoreBackup(selectedFile);
            toast.success('Backup erfolgreich wiederhergestellt');
            setRestoreDialogOpen(false);
            setSelectedFile(null);
            window.location.reload();
        } catch (error) {
            toast.error('Wiederherstellung fehlgeschlagen');
        } finally {
            setRestoring(false);
        }
    };
    
    // ============ DESIGN HANDLERS ============
    
    const handleColorChange = (key, value) => {
        setColors(prev => ({ ...prev, [key]: value }));
        setActivePresetId(null); // manuelle Änderung → keine Vorlage mehr „aktiv" markieren
    };
    
    const handleMetaChange = (key, value) => {
        setMeta(prev => ({ ...prev, [key]: value }));
    };
    
    const handleSaveColors = async () => {
        setSavingDesign(true);
        try {
            await designApi.saveColors(colors);
            applyColors(colors);
            toast.success('Farben gespeichert und angewendet!');
        } catch (error) {
            toast.error('Fehler beim Speichern der Farben');
        } finally {
            setSavingDesign(false);
        }
    };
    
    const handleSaveMeta = async () => {
        setSavingDesign(true);
        try {
            await designApi.saveMeta(meta);
            toast.success('Einstellungen gespeichert und ZIP aktualisiert!');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setSavingDesign(false);
        }
    };
    
    const handleResetDesign = async () => {
        if (!window.confirm('Alle Design-Einstellungen auf Standard zurücksetzen?')) return;
        
        try {
            await designApi.reset();
            setColors(DEFAULT_COLORS);
            applyColors(DEFAULT_COLORS);
            setMeta({
                font_family: 'Titillium Web',
                meta_title: 'BGPR Verwaltung',
                meta_description: 'Verwaltungssystem für Herzsportgruppen',
                meta_keywords: '',
                meta_robots: 'noindex, nofollow',
                custom_css: '',
            });
            toast.success('Design auf Standard zurückgesetzt!');
        } catch (error) {
            toast.error('Fehler beim Zurücksetzen');
        }
    };
    
    // ============ FONT HANDLERS ============
    
    const handleFontUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const validExtensions = ['.ttf', '.otf', '.woff', '.woff2', '.eot'];
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        
        if (!validExtensions.includes(ext)) {
            toast.error(`Ungültiger Dateityp. Erlaubt: ${validExtensions.join(', ')}`);
            return;
        }
        
        setUploadingFont(true);
        try {
            await fontApi.upload(file);
            toast.success(`Schrift "${file.name}" hochgeladen!`);
            loadFonts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
        } finally {
            setUploadingFont(false);
            if (fontInputRef.current) {
                fontInputRef.current.value = '';
            }
        }
    };
    
    const handleDeleteFont = async (filename) => {
        if (!window.confirm(`Schrift "${filename}" wirklich löschen?`)) return;
        
        try {
            await fontApi.delete(filename);
            toast.success(`Schrift "${filename}" gelöscht!`);
            loadFonts();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };
    
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    if (loading) {
        return (
            <AdminLayout title="Einstellungen">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Einstellungen">
            <PageHeader 
                title="Einstellungen" 
                description="Konfiguration und Systemverwaltung"
            />

            <Tabs value={activeTab} className="mt-6">

                {/* App-Version & Deployment Tab */}
                <TabsContent value="php-version">
                    <div className="space-y-6">
                        {/* App-Versionsanzeige */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Package className="h-5 w-5 text-primary" />
                                    App-Version
                                    <span className="bg-primary text-primary-foreground text-sm px-3 py-1 rounded-full font-mono ml-2">
                                        v{appVersion}
                                    </span>
                                </CardTitle>
                                <CardDescription>
                                    Aktuelle Versionsnummer der BGPR-Akademie. {versionBuild && <>Build vom {(/^(\d{4})-(\d{2})-(\d{2})/.exec(versionBuild) || []).slice(1).reverse().join('.') || versionBuild}.</>}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-wrap items-end gap-4">
                                    <div className="flex-1 min-w-[200px]">
                                        <Label className="text-sm font-medium mb-2 block">Versionsnummer</Label>
                                        <Input
                                            value={appVersion}
                                            onChange={(e) => setAppVersion(e.target.value)}
                                            placeholder="1.0.0"
                                            className="font-mono text-lg"
                                            data-testid="settings-app-version-input"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" size="sm" onClick={() => incrementVersion('patch')} title="Patch erhöhen">
                                            +0.0.1
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => incrementVersion('minor')} title="Minor erhöhen">
                                            +0.1.0
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => incrementVersion('major')} title="Major erhöhen">
                                            +1.0.0
                                        </Button>
                                    </div>
                                    <Button
                                        onClick={handleSaveVersion}
                                        disabled={savingVersion}
                                        data-testid="settings-save-version-btn"
                                    >
                                        {savingVersion ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                        Version setzen
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Automatisches Backup nach Dropbox */}
                        <AutoBackupCard />

                        {/* Pre-Deployment-Checkliste */}
                        <Card className="border-amber-200 bg-amber-50/30">
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Shield className="h-5 w-5 text-amber-700" />
                                    Pre-Deployment-Checkliste
                                </CardTitle>
                                <CardDescription>
                                    Vor dem Live-Gang nacheinander abarbeiten — idealerweise von oben nach unten.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* SCHRITT 1: Backup */}
                                <div className="bg-white border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-bold">1</div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-900">Backup-Snapshot ziehen</p>
                                        <p className="text-xs text-gray-600">JSON-Dump aller wichtigen Collections (Users, Seminare, Buchungen, Settings) als Sicherheitsnetz vor dem Cleanup.</p>
                                    </div>
                                    <Button
                                        onClick={handleBackupNow}
                                        disabled={backupDownloading}
                                        variant="outline"
                                        className="border-amber-400"
                                        data-testid="deploy-backup-btn"
                                    >
                                        {backupDownloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                                        Backup herunterladen
                                    </Button>
                                </div>

                                {/* SCHRITT 2: Test-Daten Cleanup */}
                                <div className="bg-white border border-amber-200 rounded-lg p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-bold">2</div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-gray-900">Test-/Demo-Daten entfernen</p>
                                            <p className="text-xs text-gray-600 mb-2">
                                                Heuristisch erkannt anhand von E-Mails mit <code className="text-[10px] bg-gray-100 px-1 rounded">+test</code>, <code className="text-[10px] bg-gray-100 px-1 rounded">@example.*</code> oder Test-Namen (Max, Erika, Testkandidat). Buchungen, Lizenzen und Rechnungen werden mitgelöscht.
                                            </p>
                                            {readiness?.test_data && (
                                                <div className="mt-2 space-y-1">
                                                    {readiness.test_data.users?.length > 0 && (
                                                        <div className="text-xs">
                                                            <span className="font-medium text-amber-900">Erkannt ({readiness.test_data.users.length} User):</span>
                                                            <span className="text-gray-700"> {readiness.test_data.users.slice(0,4).map(u => u.name || u.email).join(', ')}{readiness.test_data.users.length > 4 ? ' …' : ''}</span>
                                                        </div>
                                                    )}
                                                    {readiness.test_data.instructors?.length > 0 && (
                                                        <div className="text-xs">
                                                            <span className="font-medium text-amber-900">Mitglieder ({readiness.test_data.instructors.length}):</span>
                                                            <span className="text-gray-700"> {readiness.test_data.instructors.slice(0,4).map(i => i.name || i.email).join(', ')}{readiness.test_data.instructors.length > 4 ? ' …' : ''}</span>
                                                        </div>
                                                    )}
                                                    {readiness.test_data.users?.length === 0 && readiness.test_data.instructors?.length === 0 && (
                                                        <p className="text-xs text-emerald-700 font-medium flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Keine Test-Daten gefunden — sauber!</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <Button
                                            onClick={handleCleanupTestData}
                                            disabled={cleanupRunning || !readiness || ((readiness?.test_data?.users?.length || 0) + (readiness?.test_data?.instructors?.length || 0) === 0)}
                                            variant="outline"
                                            className="border-amber-400 text-amber-900"
                                            data-testid="deploy-cleanup-btn"
                                        >
                                            {cleanupRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                                            Test-Daten löschen
                                        </Button>
                                    </div>
                                </div>

                                {/* SCHRITT 3: Verwaiste Datensätze bereinigen */}
                                <div className="bg-white border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-bold">3</div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-900">Verwaiste Datensätze bereinigen</p>
                                        <p className="text-xs text-gray-600">Entfernt Lizenzen, Verlängerungsanträge, Buchungen, Rechnungen und Training-Records, deren zugehöriges Akademie-Mitglied bereits gelöscht wurde. Seminare bleiben unangetastet.</p>
                                    </div>
                                    <Button
                                        onClick={handleCleanupOrphans}
                                        disabled={cleanupRunning}
                                        variant="outline"
                                        className="border-amber-400 text-amber-900"
                                        data-testid="deploy-cleanup-orphans-btn"
                                    >
                                        {cleanupRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                                        Verwaiste Daten löschen
                                    </Button>
                                </div>

                                {/* SCHRITT 3b: Buchhaltung zurücksetzen (für Frisch-Start) */}
                                <div className="bg-white border border-rose-200 rounded-lg p-4 space-y-3">
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-rose-100 text-rose-800 flex items-center justify-center font-bold">3b</div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-gray-900">Buchhaltung &amp; Buchungen komplett zurücksetzen</p>
                                            <p className="text-xs text-gray-600 mt-0.5">
                                                Setzt das System auf einen <strong>frischen Buchhaltungsstand</strong> zurück: löscht <strong>alle</strong> Buchungen, Rechnungen (offen + bezahlt), Stornierungen und Zahlungs-Belege —
                                                unabhängig vom Status. <strong>Mitglieder, Lizenzen und Seminare bleiben unangetastet.</strong>
                                            </p>
                                            <p className="text-xs text-rose-700 mt-1">
                                                Nicht rückgängig zu machen. Vorher unbedingt Schritt 1 (Backup) ausführen.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <Input
                                            value={resetBookkeepingConfirm}
                                            onChange={(e) => setResetBookkeepingConfirm(e.target.value)}
                                            placeholder="Zum Bestätigen 'BUCHHALTUNG-ZURUECKSETZEN' eintippen"
                                            className="font-mono text-xs"
                                            disabled={resetBookkeepingRunning}
                                            data-testid="reset-bookkeeping-confirm-input"
                                        />
                                        <Button
                                            onClick={handleResetBookkeeping}
                                            disabled={resetBookkeepingRunning || resetBookkeepingConfirm !== 'BUCHHALTUNG-ZURUECKSETZEN'}
                                            variant="destructive"
                                            data-testid="reset-bookkeeping-btn"
                                        >
                                            {resetBookkeepingRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                                            Buchhaltung zurücksetzen
                                        </Button>
                                    </div>
                                </div>

                                {/* SCHRITT 4: SMTP-Test */}
                                <div className="bg-white border border-amber-200 rounded-lg p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-bold">4</div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-gray-900">SMTP-Live-Test</p>
                                            <p className="text-xs text-gray-600 mb-2">
                                                {readiness?.smtp_configured
                                                    ? <>SMTP konfiguriert (Absender: <code className="text-[10px] bg-gray-100 px-1 rounded">{readiness.smtp_from}</code>). Sendet eine echte Test-Mail über den hinterlegten Server.</>
                                                    : <>⚠️ SMTP ist <strong>nicht</strong> konfiguriert. Bitte zuerst unter Mail → SMTP einrichten.</>}
                                            </p>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="email"
                                                    value={smtpTestTo}
                                                    onChange={(e) => setSmtpTestTo(e.target.value)}
                                                    placeholder="empfaenger@deine-domain.de"
                                                    className="text-sm"
                                                    data-testid="deploy-smtp-test-input"
                                                />
                                                <Button
                                                    onClick={handleSendSmtpTest}
                                                    disabled={smtpTestSending || !readiness?.smtp_configured}
                                                    variant="outline"
                                                    className="border-amber-400"
                                                    data-testid="deploy-smtp-test-btn"
                                                >
                                                    {smtpTestSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                                                    Senden
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* SCHRITT 5: Maintenance-Status */}
                                <div className="bg-white border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-bold">5</div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-900">Wartungsmodus prüfen</p>
                                        <p className="text-xs text-gray-600">
                                            Status: {readinessLoading
                                                ? <Loader2 className="inline h-3 w-3 animate-spin" />
                                                : readiness?.maintenance_enabled
                                                    ? <span className="text-rose-700 font-semibold">AN — öffentliche Seite zeigt Wartungshinweis</span>
                                                    : <span className="text-emerald-700 font-semibold">AUS — Seite ist öffentlich erreichbar ✓</span>}
                                        </p>
                                    </div>
                                    {readiness?.maintenance_enabled ? (
                                        <Button
                                            onClick={() => handleToggleMaintenance(false)}
                                            disabled={maintenanceToggling}
                                            variant="outline"
                                            className="border-emerald-400 text-emerald-900"
                                            data-testid="deploy-maintenance-off"
                                        >
                                            {maintenanceToggling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                            Wartungsmodus ausschalten
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={() => handleToggleMaintenance(true)}
                                            disabled={maintenanceToggling}
                                            variant="outline"
                                            data-testid="deploy-maintenance-on"
                                        >
                                            {maintenanceToggling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                            Wartungsmodus aktivieren
                                        </Button>
                                    )}
                                </div>

                                {/* Datenbank-Counts als Sanity-Anzeige */}
                                {readiness?.counts && (
                                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 pt-2">
                                        {[
                                            { label: 'User', val: readiness.counts.users_total },
                                            { label: 'Mitglieder', val: readiness.counts.instructors_total },
                                            { label: 'Seminare', val: readiness.counts.seminars_total },
                                            { label: 'Buchungen', val: readiness.counts.bookings_total },
                                            { label: 'Lizenzen', val: readiness.counts.licenses_total },
                                            { label: 'Rechnungen', val: readiness.counts.invoices_total },
                                        ].map((c) => (
                                            <div key={c.label} className="text-center bg-white border border-amber-200/60 rounded-md py-1.5">
                                                <div className="text-xs text-gray-500">{c.label}</div>
                                                <div className="text-base font-semibold text-amber-900">{c.val}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Native Deployment Hinweis */}
                        <Card className="border-emerald-200 bg-emerald-50/40">
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                                    Bereit für Live-Deployment
                                </CardTitle>
                                <CardDescription>
                                    Diese App wird direkt über Emergent live geschaltet – kein Export, kein FTP, kein Hosting-Drama.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid sm:grid-cols-3 gap-3 text-sm">
                                    <div className="p-3 bg-white border border-emerald-200 rounded-lg">
                                        <p className="font-semibold text-emerald-900 mb-1 flex items-center gap-2">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">1</span>
                                            Deployen
                                        </p>
                                        <p className="text-emerald-800/80 text-xs leading-relaxed">
                                            Im Chat-Eingabefeld rechts oben auf <strong>„Deploy"</strong> klicken. Emergent baut die App in 5–10 Min produktionsreif.
                                        </p>
                                    </div>
                                    <div className="p-3 bg-white border border-emerald-200 rounded-lg">
                                        <p className="font-semibold text-emerald-900 mb-1 flex items-center gap-2">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">2</span>
                                            Domain anbinden
                                        </p>
                                        <p className="text-emerald-800/80 text-xs leading-relaxed">
                                            Im Deployment-Dashboard <strong>bgpr-akademie.de</strong> verknüpfen. Entri-Wizard übernimmt die DNS-Konfiguration.
                                        </p>
                                    </div>
                                    <div className="p-3 bg-white border border-emerald-200 rounded-lg">
                                        <p className="font-semibold text-emerald-900 mb-1 flex items-center gap-2">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">3</span>
                                            Live
                                        </p>
                                        <p className="text-emerald-800/80 text-xs leading-relaxed">
                                            DNS-Propagation 5–15 Min, dann ist <strong>https://bgpr-akademie.de</strong> erreichbar – mit allem, was Du in der Preview siehst.
                                        </p>
                                    </div>
                                </div>
                                <div className="p-4 bg-white border border-emerald-200 rounded-lg text-sm text-emerald-900">
                                    <p className="font-semibold mb-1 flex items-center gap-2">
                                        <Shield className="h-4 w-4" /> Was beim Deployment passiert
                                    </p>
                                    <ul className="list-disc list-inside space-y-1 text-emerald-800/90 text-xs leading-relaxed">
                                        <li>React-Frontend, FastAPI-Backend und MongoDB werden 1:1 produktiv geschaltet.</li>
                                        <li>Alle aktuellen Einstellungen (Design, Logos, Templates, Rechtstexte, SMTP, Dropbox) bleiben erhalten.</li>
                                        <li>Test-/Demo-Daten kannst Du vor dem Go-Live aufräumen (siehe Tab <strong>System</strong>).</li>
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>


                {/* Design Tab */}
                <TabsContent value="design">
                    <div className="space-y-6">
                        {/* Design Sub-Tabs */}
                        <div className="flex gap-2 border-b pb-3">
                            <Button 
                                variant={designTab === 'colors' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setDesignTab('colors')}
                            >
                                <Palette className="h-4 w-4 mr-2" />
                                Farben
                            </Button>
                            <Button 
                                variant={designTab === 'meta' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setDesignTab('meta')}
                            >
                                <Search className="h-4 w-4 mr-2" />
                                SEO & Meta-Tags
                            </Button>
                        </div>
                        
                        {designTab === 'colors' && (
                            <div className="grid lg:grid-cols-3 gap-6">
                                {/* Preset Selection */}
                                <div className="lg:col-span-3">
                                    <Card className="border-dashed">
                                        <CardHeader className="pb-3">
                                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                                <div>
                                                    <CardTitle className="text-base flex items-center gap-2">
                                                        <Palette className="h-4 w-4" />
                                                        Farbvorlage wählen
                                                    </CardTitle>
                                                    <CardDescription>Klicken Sie auf eine Vorlage, um die Farben zu übernehmen. Anschließend können Sie speichern oder weiter anpassen.</CardDescription>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        ref={presetImportInputRef}
                                                        type="file"
                                                        accept="application/json,.json"
                                                        className="hidden"
                                                        onChange={handleImportPresetFile}
                                                        data-testid="preset-import-input"
                                                    />
                                                    <Button variant="outline" size="sm" onClick={handleImportPresetClick} data-testid="preset-import-btn">
                                                        <FileUp className="h-4 w-4 mr-1.5" /> JSON importieren
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="flex gap-3 flex-wrap">
                                                {[
                                                    ...COLOR_PRESETS.map(p => ({ ...p, is_builtin: true })),
                                                    ...customPresets.map(p => ({
                                                        id: p.id,
                                                        name: p.name,
                                                        colors: p.colors,
                                                        accent: p.colors?.primary || '#0092B5',
                                                        secondary: p.colors?.secondary || '#1F2937',
                                                        is_builtin: false,
                                                    })),
                                                ].map((preset) => {
                                                    const isActive = activePresetId === preset.id;
                                                    const isRenaming = renamingPresetId === preset.id;
                                                    return (
                                                        <div
                                                            key={preset.id}
                                                            className={`group relative flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                                                                isActive ? 'border-primary bg-primary/5 shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                                            }`}
                                                            data-testid={`preset-${preset.id}`}
                                                            onClick={() => { if (!isRenaming) { setColors({ ...preset.colors }); setActivePresetId(preset.id); } }}
                                                        >
                                                            <div className="flex gap-1">
                                                                <div className="w-6 h-6 rounded-full border border-white shadow-sm" style={{ backgroundColor: preset.accent }} />
                                                                <div className="w-6 h-6 rounded-full border border-white shadow-sm" style={{ backgroundColor: preset.secondary }} />
                                                            </div>
                                                            <div className="text-left min-w-[140px]">
                                                                {isRenaming ? (
                                                                    <Input
                                                                        autoFocus
                                                                        value={renameValue}
                                                                        onChange={(e) => setRenameValue(e.target.value)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') handleRenamePreset(preset.id);
                                                                            if (e.key === 'Escape') setRenamingPresetId(null);
                                                                        }}
                                                                        onBlur={() => handleRenamePreset(preset.id)}
                                                                        className="h-7 text-sm"
                                                                        data-testid={`preset-rename-input-${preset.id}`}
                                                                    />
                                                                ) : (
                                                                    <p className="text-sm font-medium flex items-center gap-1.5">
                                                                        {preset.name}
                                                                        {preset.is_builtin && (
                                                                            <span className="text-[9px] uppercase tracking-wide bg-gray-100 text-gray-600 px-1 py-0.5 rounded">Standard</span>
                                                                        )}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            {isActive && !isRenaming && (
                                                                <CheckCircle className="h-4 w-4 text-primary" />
                                                            )}
                                                            {/* Action icons */}
                                                            <div className="flex items-center gap-0.5 ml-1 opacity-60 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    type="button"
                                                                    title="Duplizieren – Kopie zum Bearbeiten anlegen"
                                                                    onClick={(e) => { e.stopPropagation(); handleDuplicatePreset(preset); }}
                                                                    className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800"
                                                                    data-testid={`preset-duplicate-${preset.id}`}
                                                                >
                                                                    <Copy className="h-3.5 w-3.5" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title="Als JSON-Datei exportieren"
                                                                    onClick={(e) => { e.stopPropagation(); handleExportPreset(preset); }}
                                                                    className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800"
                                                                    data-testid={`preset-export-${preset.id}`}
                                                                >
                                                                    <Download className="h-3.5 w-3.5" />
                                                                </button>
                                                                {!preset.is_builtin && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            title="Aktuelle Farben in dieser Vorlage speichern"
                                                                            onClick={(e) => { e.stopPropagation(); handleSavePresetColors(preset.id); }}
                                                                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-emerald-700"
                                                                            data-testid={`preset-save-${preset.id}`}
                                                                        >
                                                                            <Save className="h-3.5 w-3.5" />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            title="Umbenennen"
                                                                            onClick={(e) => { e.stopPropagation(); setRenameValue(preset.name); setRenamingPresetId(preset.id); }}
                                                                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800"
                                                                            data-testid={`preset-rename-${preset.id}`}
                                                                        >
                                                                            <Pencil className="h-3.5 w-3.5" />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            title="Vorlage löschen"
                                                                            onClick={(e) => { e.stopPropagation(); handleDeletePreset(preset.id, preset.name); }}
                                                                            className="p-1.5 rounded hover:bg-red-50 text-gray-500 hover:text-red-600"
                                                                            data-testid={`preset-delete-${preset.id}`}
                                                                        >
                                                                            <Trash2 className="h-3.5 w-3.5" />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-3">
                                                Tipp: Eine Vorlage <strong>duplizieren</strong> (Kopier-Symbol), um sie ohne Risiko anzupassen.
                                                Mit dem <strong>Disketten-Symbol</strong> speichern Sie die aktuell eingestellten Farben in eine eigene Vorlage zurück.
                                            </p>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Color Inputs */}
                                <div className="lg:col-span-2 space-y-6">
                                    {Object.entries(COLOR_GROUPS).map(([groupName, groupColors]) => (
                                        <Card key={groupName}>
                                            <CardHeader className="pb-3">
                                                <CardTitle className="text-base">{groupName}</CardTitle>
                                            </CardHeader>
                                            <CardContent className="space-y-4">
                                                {Object.entries(groupColors).map(([colorKey, colorInfo]) => (
                                                    <div key={colorKey} className="flex items-center gap-4">
                                                        <input 
                                                            type="color"
                                                            value={colors[colorKey]}
                                                            onChange={(e) => handleColorChange(colorKey, e.target.value)}
                                                            className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200 p-1"
                                                        />
                                                        <div className="flex-1">
                                                            <p className="font-medium text-sm">{colorInfo.label}</p>
                                                            <p className="text-xs text-gray-500">{colorInfo.desc}</p>
                                                        </div>
                                                        <Input 
                                                            value={colors[colorKey]}
                                                            onChange={(e) => handleColorChange(colorKey, e.target.value)}
                                                            className="w-24 font-mono text-sm uppercase"
                                                            placeholder="#000000"
                                                        />
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm"
                                                            onClick={() => handleColorChange(colorKey, DEFAULT_COLORS[colorKey])}
                                                            title="Auf Standard zurücksetzen"
                                                        >
                                                            <RefreshCw className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                ))}
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                                
                                {/* Preview & Actions */}
                                <div className="space-y-4">
                                    <Card>
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <Eye className="h-4 w-4" />
                                                Live-Vorschau
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="rounded-lg overflow-hidden border border-gray-200" style={{fontSize: '10px'}}>
                                                <div className="flex">
                                                    <div className="w-16 p-2" style={{backgroundColor: colors.sidebar_bg}}>
                                                        <div className="w-6 h-6 rounded mb-2 mx-auto" style={{backgroundColor: colors.primary}}></div>
                                                        <div className="space-y-1">
                                                            <div className="h-2 rounded" style={{backgroundColor: colors.sidebar_active}}></div>
                                                            <div className="h-2 rounded" style={{backgroundColor: colors.sidebar_text}}></div>
                                                            <div className="h-2 rounded" style={{backgroundColor: colors.sidebar_text}}></div>
                                                        </div>
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="h-6 px-2 flex items-center" style={{backgroundColor: colors.header_bg}}>
                                                            <div className="w-8 h-2 rounded" style={{backgroundColor: colors.text_primary}}></div>
                                                        </div>
                                                        <div className="p-2 min-h-[100px]" style={{backgroundColor: colors.body_bg}}>
                                                            <div className="rounded p-2 mb-2" style={{backgroundColor: colors.card_bg}}>
                                                                <div className="h-2 w-16 rounded mb-1" style={{backgroundColor: colors.text_primary}}></div>
                                                                <div className="h-1.5 w-24 rounded" style={{backgroundColor: colors.text_secondary}}></div>
                                                            </div>
                                                            <div className="flex gap-1">
                                                                <div className="h-4 w-10 rounded" style={{backgroundColor: colors.primary}}></div>
                                                                <div className="h-4 w-6 rounded" style={{backgroundColor: colors.success}}></div>
                                                                <div className="h-4 w-6 rounded" style={{backgroundColor: colors.warning}}></div>
                                                                <div className="h-4 w-6 rounded" style={{backgroundColor: colors.error}}></div>
                                                            </div>
                                                            <div className="flex gap-1 mt-1">
                                                                <div className="h-4 px-1 rounded flex items-center justify-center" style={{backgroundColor: colors.btn_primary, color: colors.btn_primary_text, fontSize: '5px'}}>Primär</div>
                                                                <div className="h-4 px-1 rounded flex items-center justify-center" style={{backgroundColor: colors.btn_edit, color: colors.btn_edit_text, fontSize: '5px'}}>Bearb.</div>
                                                                <div className="h-4 px-1 rounded flex items-center justify-center" style={{backgroundColor: colors.btn_renew, color: colors.btn_renew_text, fontSize: '5px'}}>Verl.</div>
                                                                <div className="h-4 px-1 rounded flex items-center justify-center" style={{backgroundColor: colors.btn_danger, color: colors.btn_danger_text, fontSize: '5px'}}>Lösch.</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                    
                                    <Button 
                                        onClick={handleSaveColors} 
                                        disabled={savingDesign}
                                        className="w-full"
                                    >
                                        {savingDesign ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Save className="h-4 w-4 mr-2" />
                                        )}
                                        Farben speichern
                                    </Button>
                                    
                                    <Button 
                                        variant="outline"
                                        onClick={handleResetDesign}
                                        className="w-full"
                                    >
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Alle zurücksetzen
                                    </Button>
                                    
                                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                                        <CheckCircle className="h-4 w-4 inline mr-2" />
                                        Änderungen werden automatisch ins ZIP-Paket übernommen.
                                    </div>
                                </div>

                                {/* Öffentlicher Header (Hintergrundbild + Overlay) */}
                                <div className="lg:col-span-3">
                                    <PublicHeaderImageCard
                                        colors={colors}
                                        setColors={setColors}
                                    />
                                </div>
                            </div>
                        )}
                        
                        {designTab === 'meta' && (
                            <div className="grid lg:grid-cols-2 gap-6">
                                {/* Meta Tags */}
                                <div className="space-y-6">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base">Meta-Tags</CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <div>
                                                <Label>Seitentitel (Title-Tag)</Label>
                                                <Input 
                                                    value={meta.meta_title}
                                                    onChange={(e) => handleMetaChange('meta_title', e.target.value)}
                                                    placeholder="BGPR Verwaltung"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Wird im Browser-Tab angezeigt</p>
                                            </div>
                                            
                                            <div>
                                                <Label>Beschreibung (Meta Description)</Label>
                                                <textarea 
                                                    value={meta.meta_description}
                                                    onChange={(e) => handleMetaChange('meta_description', e.target.value)}
                                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                                    rows={3}
                                                    placeholder="Verwaltungssystem für Herzsportgruppen"
                                                />
                                            </div>
                                            
                                            <div>
                                                <Label>Schlüsselwörter (Keywords)</Label>
                                                <Input 
                                                    value={meta.meta_keywords}
                                                    onChange={(e) => handleMetaChange('meta_keywords', e.target.value)}
                                                    placeholder="herzsport, bgpr, übungsleiter"
                                                />
                                            </div>
                                        </CardContent>
                                    </Card>
                                    
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base">Schriftart</CardTitle>
                                            <p className="text-xs text-muted-foreground">Wird sowohl in der App als auch als Standard-Schriftfamilie für PDF-Bescheinigungen, Lizenzen und Rechnungen verwendet.</p>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            {(() => {
                                                // Built-ins + alle hochgeladenen Schriftarten (deduped, alphabetisch)
                                                const fontOptions = Array.from(new Set([
                                                    ...AVAILABLE_FONTS,
                                                    ...fontFamilies,
                                                ])).sort((a, b) => a === 'System Standard' ? -1 : b === 'System Standard' ? 1 : a.localeCompare(b));
                                                return fontOptions.map(font => (
                                                    <label
                                                        key={font}
                                                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition ${meta.font_family === font ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="font_family"
                                                            value={font}
                                                            checked={meta.font_family === font}
                                                            onChange={(e) => handleMetaChange('font_family', e.target.value)}
                                                            className="w-4 h-4 text-red-600"
                                                            data-testid={`font-family-radio-${font.replace(/\s+/g, '-').toLowerCase()}`}
                                                        />
                                                        <span className="font-medium" style={font !== 'System Standard' ? { fontFamily: `'${font}', sans-serif` } : undefined}>{font}</span>
                                                        {fontFamilies.includes(font) && !AVAILABLE_FONTS.includes(font) && (
                                                            <span className="ml-auto px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded">Hochgeladen</span>
                                                        )}
                                                        {meta.font_family === font && (
                                                            <span className={`${fontFamilies.includes(font) && !AVAILABLE_FONTS.includes(font) ? 'ml-2' : 'ml-auto'} px-2 py-0.5 bg-red-600 text-white text-xs rounded`}>Aktiv</span>
                                                        )}
                                                    </label>
                                                ));
                                            })()}
                                        </CardContent>
                                    </Card>
                                </div>
                                
                                {/* Robots & Custom CSS */}
                                <div className="space-y-6">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base">Suchmaschinen-Einstellungen</CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            {ROBOTS_OPTIONS.map(option => (
                                                <label 
                                                    key={option.value}
                                                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition ${meta.meta_robots === option.value ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                                >
                                                    <input 
                                                        type="radio"
                                                        name="meta_robots"
                                                        value={option.value}
                                                        checked={meta.meta_robots === option.value}
                                                        onChange={(e) => handleMetaChange('meta_robots', e.target.value)}
                                                        className="w-4 h-4 text-red-600 mt-0.5"
                                                    />
                                                    <div>
                                                        <code className="text-sm font-mono">{option.value}</code>
                                                        <p className="text-xs text-gray-500">{option.label}</p>
                                                    </div>
                                                </label>
                                            ))}
                                            
                                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                                                <AlertTriangle className="h-4 w-4 inline mr-2" />
                                                <strong>Empfehlung:</strong> "noindex, nofollow" für interne Verwaltungsanwendungen
                                            </div>
                                        </CardContent>
                                    </Card>
                                    
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base">Benutzerdefiniertes CSS</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <textarea 
                                                value={meta.custom_css}
                                                onChange={(e) => handleMetaChange('custom_css', e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md font-mono text-sm"
                                                rows={6}
                                                placeholder="/* Eigene CSS-Regeln */"
                                            />
                                        </CardContent>
                                    </Card>
                                    
                                    <Button 
                                        onClick={handleSaveMeta} 
                                        disabled={savingDesign}
                                        className="w-full"
                                    >
                                        {savingDesign ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Save className="h-4 w-4 mr-2" />
                                        )}
                                        Einstellungen speichern
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </TabsContent>

                {/* Logos Tab */}
                <TabsContent value="logos">
                    <LogoSettings />
                </TabsContent>

                {/* Login-Page Tab */}
                <TabsContent value="login-page">
                    <LoginPageSettings />
                </TabsContent>

                {/* Bank Tab */}
                <TabsContent value="bank">
                    <BankSettings />
                </TabsContent>

                {/* Prerequisites Tab */}
                <TabsContent value="prerequisites">
                    <PrerequisitesSettings />
                </TabsContent>

                {/* Ausgabenkategorien Tab */}
                <TabsContent value="expense-categories">
                    <ExpenseCategoriesSettings />
                </TabsContent>

                {/* Reminders Tab */}
                <TabsContent value="reminders">
                    <ReminderSettings />
                </TabsContent>

                {/* Notifications Tab (System-Benachrichtigungen) */}
                <TabsContent value="notifications">
                    <NotificationSettings />
                </TabsContent>

                {/* Fonts Tab */}
                <TabsContent value="fonts">
                    <div className="space-y-6">
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-green-800">DSGVO-konforme Schriften</h4>
                                    <p className="text-sm text-green-700 mt-1">
                                        Laden Sie Schriftdateien hier hoch. Diese werden lokal von Ihrem Server geladen - 
                                        keine externen Google-Fonts-Aufrufe! Unterstützte Formate: .ttf, .otf, .woff, .woff2, .eot
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Upload */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Upload className="h-5 w-5" />
                                        Schrift hochladen
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <input 
                                        type="file"
                                        ref={fontInputRef}
                                        onChange={handleFontUpload}
                                        accept=".ttf,.otf,.woff,.woff2,.eot"
                                        className="hidden"
                                    />
                                    <Button 
                                        onClick={() => fontInputRef.current?.click()}
                                        disabled={uploadingFont}
                                        className="w-full"
                                    >
                                        {uploadingFont ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Upload className="h-4 w-4 mr-2" />
                                        )}
                                        Schriftdatei auswählen
                                    </Button>
                                    
                                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                                        <p className="font-medium mb-1">Titillium Web herunterladen:</p>
                                        <a 
                                            href="https://fonts.google.com/specimen/Titillium+Web"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                        >
                                            → Google Fonts (kostenlos)
                                        </a>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            {/* Existing Fonts */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Type className="h-5 w-5" />
                                        Hochgeladene Schriften ({fonts.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {fonts.length === 0 ? (
                                        <p className="text-gray-500 text-sm">Noch keine Schriften hochgeladen</p>
                                    ) : (
                                        <div className="space-y-2 max-h-64 overflow-y-auto">
                                            {fonts.map(font => (
                                                <div 
                                                    key={font.name}
                                                    className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                                                >
                                                    <Type className="h-4 w-4 text-gray-400" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-sm truncate">{font.name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {formatFileSize(font.size)} • {font.extension}
                                                        </p>
                                                    </div>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={() => handleDeleteFont(font.name)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                        
                        {/* Required fonts for Titillium Web */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Empfohlene Schriftdateien für Titillium Web</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                    {[
                                        'TitilliumWeb-Regular.woff2',
                                        'TitilliumWeb-Light.woff2',
                                        'TitilliumWeb-SemiBold.woff2',
                                        'TitilliumWeb-Bold.woff2',
                                        'TitilliumWeb-Italic.woff2',
                                    ].map(name => {
                                        const exists = fonts.some(f => f.name.toLowerCase() === name.toLowerCase());
                                        return (
                                            <div 
                                                key={name}
                                                className={`flex items-center gap-2 p-2 rounded ${exists ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}
                                            >
                                                {exists ? (
                                                    <CheckCircle className="h-4 w-4" />
                                                ) : (
                                                    <XCircle className="h-4 w-4" />
                                                )}
                                                <span>{name}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Widget Tab */}
                <TabsContent value="widget">
                    <div className="space-y-6">
                        {/* Empfohlene Methode: JavaScript */}
                        <Card className="border-green-200 bg-green-50/30">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                    JavaScript-Widget (Empfohlen)
                                    <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full ml-2">NAHTLOSE INTEGRATION</span>
                                </CardTitle>
                                <CardDescription>
                                    Das Widget passt sich automatisch an Ihre Seite an – ohne Scrollbalken, ohne sichtbaren Rahmen.
                                    Perfekt für Joomla, WordPress und andere CMS.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                                    <pre className="text-sm text-gray-300">
                                        <code>{`<!-- BGPR Veranstaltungskalender -->
<div id="bgpr-events"></div>
<script src="https://ihre-domain.de/widget.js"></script>`}</code>
                                    </pre>
                                </div>
                                <Button 
                                    onClick={() => {
                                        navigator.clipboard.writeText(`<!-- BGPR Veranstaltungskalender -->
<div id="bgpr-events"></div>
<script src="https://ihre-domain.de/widget.js"></script>`);
                                        toast.success('JavaScript-Code kopiert!');
                                    }}
                                    className="bg-green-600 hover:bg-green-700"
                                >
                                    <Code className="h-4 w-4 mr-2" />
                                    Code kopieren
                                </Button>
                                
                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-sm text-blue-800">
                                        <strong>Hinweis:</strong> Ersetzen Sie <code className="bg-blue-100 px-1 rounded">ihre-domain.de</code> durch 
                                        die URL Ihrer installierten BGPR-App.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Optionale Konfiguration */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Optionale Konfiguration</CardTitle>
                                <CardDescription>
                                    Passen Sie das Widget an Ihre Seite an (fügen Sie dies VOR dem Script-Tag ein)
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                                    <pre className="text-sm text-gray-300">
                                        <code>{`<script>
var bgprWidgetConfig = {
    showHeader: true,       // Header anzeigen (true/false)
    maxEvents: 10,          // Max. Anzahl Veranstaltungen
    primaryColor: '#CC0000' // Ihre Hauptfarbe
};
</script>`}</code>
                                    </pre>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Alternative: iFrame */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Alternative: iFrame</CardTitle>
                                <CardDescription>
                                    Falls JavaScript nicht möglich ist – funktioniert überall, aber mit festem Rahmen
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                                    <pre className="text-sm text-gray-300">
                                        <code>{`<iframe 
  src="https://ihre-domain.de/widget.php"
  width="100%" 
  height="600" 
  frameborder="0"
  style="border: none; border-radius: 8px;"
></iframe>`}</code>
                                    </pre>
                                </div>
                                <Button 
                                    variant="outline"
                                    onClick={() => {
                                        navigator.clipboard.writeText(`<iframe 
  src="https://ihre-domain.de/widget.php"
  width="100%" 
  height="600" 
  frameborder="0"
  style="border: none; border-radius: 8px;"
></iframe>`);
                                        toast.success('iFrame-Code kopiert!');
                                    }}
                                >
                                    <Code className="h-4 w-4 mr-2" />
                                    iFrame-Code kopieren
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Features des Widgets */}
                        <div className="grid sm:grid-cols-3 gap-4">
                            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                                <div>
                                    <p className="font-medium text-sm">Keine Scrollbalken</p>
                                    <p className="text-xs text-gray-500">Passt sich automatisch an</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                                <div>
                                    <p className="font-medium text-sm">Responsive</p>
                                    <p className="text-xs text-gray-500">Mobile-optimiert</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                                <div>
                                    <p className="font-medium text-sm">Live-Daten</p>
                                    <p className="text-xs text-gray-500">Immer aktuell aus der DB</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </TabsContent>

                {/* SMTP Tab */}
                <TabsContent value="smtp">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <Mail className="h-5 w-5" />
                                E-Mail Konfiguration (SMTP)
                            </CardTitle>
                            <CardDescription>
                                Konfigurieren Sie den E-Mail-Versand für Benachrichtigungen
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Server Settings */}
                            <div>
                                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Server</h3>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="host">SMTP Server</Label>
                                        <Input
                                            id="host"
                                            name="host"
                                            value={smtpSettings.host}
                                            onChange={handleSmtpChange}
                                            placeholder="smtp.example.com"
                                            data-testid="smtp-host"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="port">Port</Label>
                                        <Input
                                            id="port"
                                            name="port"
                                            type="number"
                                            value={smtpSettings.port}
                                            onChange={handleSmtpChange}
                                            placeholder="587"
                                            data-testid="smtp-port"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="username">Benutzername</Label>
                                        <Input
                                            id="username"
                                            name="username"
                                            value={smtpSettings.username}
                                            onChange={handleSmtpChange}
                                            placeholder="user@example.com"
                                            data-testid="smtp-username"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="password">Passwort</Label>
                                        <Input
                                            id="password"
                                            name="password"
                                            type="password"
                                            value={smtpSettings.password}
                                            onChange={handleSmtpChange}
                                            placeholder="••••••••"
                                            data-testid="smtp-password"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2 mt-4">
                                    <Switch
                                        id="use_tls"
                                        checked={smtpSettings.use_tls}
                                        onCheckedChange={(checked) =>
                                            setSmtpSettings({ ...smtpSettings, use_tls: checked })
                                        }
                                    />
                                    <Label htmlFor="use_tls">TLS/STARTTLS verwenden</Label>
                                </div>
                            </div>

                            {/* Sender Settings */}
                            <div>
                                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Absender</h3>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="from_email">Absender E-Mail</Label>
                                        <Input
                                            id="from_email"
                                            name="from_email"
                                            type="email"
                                            value={smtpSettings.from_email}
                                            onChange={handleSmtpChange}
                                            placeholder="noreply@bgpr.org"
                                            data-testid="smtp-from-email"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="from_name">Absender Name</Label>
                                        <Input
                                            id="from_name"
                                            name="from_name"
                                            value={smtpSettings.from_name}
                                            onChange={handleSmtpChange}
                                            placeholder="BGPR Verwaltung"
                                            data-testid="smtp-from-name"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Reply-To Settings */}
                            <div>
                                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Antwortadresse (Reply-To)</h3>
                                <p className="text-sm text-muted-foreground mb-3">
                                    Wenn Sie mit einer noreply-Adresse versenden, können Empfänger an diese Adresse antworten.
                                </p>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="reply_to_email">Antwort-E-Mail</Label>
                                        <Input
                                            id="reply_to_email"
                                            name="reply_to_email"
                                            type="email"
                                            value={smtpSettings.reply_to_email}
                                            onChange={handleSmtpChange}
                                            placeholder="info@bgpr.org"
                                            data-testid="smtp-reply-to-email"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="reply_to_name">Antwort-Name</Label>
                                        <Input
                                            id="reply_to_name"
                                            name="reply_to_name"
                                            value={smtpSettings.reply_to_name}
                                            onChange={handleSmtpChange}
                                            placeholder="BGPR e.V."
                                            data-testid="smtp-reply-to-name"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <Button onClick={handleSaveSmtp} disabled={saving} data-testid="save-smtp-button">
                                    {saving ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Save className="mr-2 h-4 w-4" />
                                    )}
                                    Speichern
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* SMTP Test */}
                    <SmtpTestCard />
                </TabsContent>

                {/* Email Signature Tab */}
                <TabsContent value="email-signature">
                    <EmailSignatureSettings />
                </TabsContent>

                {/* Audit Log Tab */}
                <TabsContent value="audit-log">
                    <AuditLogSettings />
                </TabsContent>


                {/* General Settings Tab */}
                <TabsContent value="general">
                    <div className="space-y-6">
                        <LiveUrlSettings />
                        <AkademieUrlSettings />
                        <MaintenanceModeCard />
                        <PublicModulesSettings />
                        <LicenseWarningSettings />
                        <QualificationSettings />
                        <LicenseTypeSettings />
                    </div>
                </TabsContent>

                {/* Cancellations Tab */}
                <TabsContent value="cancellations">
                    <div className="space-y-6">
                        <CancellationRulesCard />
                        <CancellationReasonsCard />
                    </div>
                </TabsContent>

                {/* System Tab */}
                <TabsContent value="system">
                    <div className="space-y-6">
                        {/* System Status */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Server className="h-5 w-5" />
                                    Systemstatus
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid sm:grid-cols-3 gap-4">
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                        <Code className="h-5 w-5 text-gray-500" />
                                        <div>
                                            <p className="text-sm text-gray-500">Version</p>
                                            <p className="font-semibold">{systemStatus?.version || '1.0.0'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                        <Database className="h-5 w-5 text-gray-500" />
                                        <div>
                                            <p className="text-sm text-gray-500">Datenbank</p>
                                            <p className="font-semibold flex items-center gap-1">
                                                {systemStatus?.database === 'connected' ? (
                                                    <>
                                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                                        Verbunden
                                                    </>
                                                ) : (
                                                    <>
                                                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                                                        Nicht verbunden
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                        <HardDrive className="h-5 w-5 text-gray-500" />
                                        <div>
                                            <p className="text-sm text-gray-500">Speicher</p>
                                            <p className="font-semibold">{systemStatus?.disk_usage || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Backup & Export */}
                        <div className="grid md:grid-cols-2 gap-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading flex items-center gap-2">
                                        <Database className="h-5 w-5" />
                                        Datensicherung
                                    </CardTitle>
                                    <CardDescription>
                                        Backup erstellen und wiederherstellen
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <Button onClick={handleBackup} className="w-full">
                                        <Download className="mr-2 h-4 w-4" />
                                        Backup erstellen
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => setRestoreDialogOpen(true)}
                                        className="w-full"
                                    >
                                        <Upload className="mr-2 h-4 w-4" />
                                        Backup wiederherstellen
                                    </Button>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading flex items-center gap-2">
                                        <RefreshCw className="h-5 w-5" />
                                        System-Update
                                    </CardTitle>
                                    <CardDescription>
                                        Prüfen auf Updates und anwenden
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <Button
                                        onClick={handleSystemUpdate}
                                        disabled={updating}
                                        className="w-full"
                                    >
                                        {updating ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                            <RefreshCw className="mr-2 h-4 w-4" />
                                        )}
                                        Auf Updates prüfen
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Performance & Ressourcen */}
                        <PerformanceStatsCard />

                        {/* Session / Auto-Logout */}
                        <SessionSettingsCard />

                        {/* Seminar-Kapazität */}
                        <SeminarCapacityCard />
                    </div>
                </TabsContent>

                {/* Session / Auto-Logout */}
                <TabsContent value="session">
                    <SessionSettingsCard />
                </TabsContent>

                {/* Benutzerverwaltung (nur Super-Admin) */}
                <TabsContent value="user-management">
                    <UserManagementCard />
                </TabsContent>

                {/* Admin-Berechtigungs-Matrix (nur Super-Admin) */}
                <TabsContent value="admin-permissions">
                    <AdminPermissionsCard />
                </TabsContent>

                {/* Legal Tab */}
                <TabsContent value="legal">
                    <LegalTextsSettings />
                </TabsContent>

                {/* Renewal Rules Tab */}
                <TabsContent value="renewal-rules">
                    <div className="space-y-6">
                        <RenewalRulesSettings />
                        <RejectionReasonsSettings />
                    </div>
                </TabsContent>

                {/* Dropbox Tab */}
                <TabsContent value="dropbox">
                    <DropboxSettings />
                </TabsContent>
            </Tabs>

            {/* Restore Dialog */}
            <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Backup wiederherstellen</DialogTitle>
                        <DialogDescription>
                            Wählen Sie eine Backup-Datei aus. Alle aktuellen Daten werden überschrieben.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-semibold">Warnung</p>
                                    <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
                                </div>
                            </div>
                        </div>
                        <div>
                            <input
                                type="file"
                                accept=".json"
                                onChange={handleFileSelect}
                                ref={fileInputRef}
                                className="hidden"
                            />
                            <Button
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full"
                            >
                                <FileUp className="mr-2 h-4 w-4" />
                                {selectedFile ? selectedFile.name : 'Backup-Datei auswählen'}
                            </Button>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRestoreDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleRestore}
                            disabled={!selectedFile || restoring}
                            variant="destructive"
                        >
                            {restoring ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            Wiederherstellen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}


function LiveUrlSettings() {
    const [liveUrl, setLiveUrl] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        api.get('/settings/live-url')
            .then(res => setLiveUrl(res.data.live_url || ''))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.post('/settings/live-url', { live_url: liveUrl });
            toast.success('Live-URL gespeichert');
        } catch { toast.error('Fehler beim Speichern'); }
        finally { setSaving(false); }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Live-App URL
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                    Die URL Ihrer Live-Installation. Wird für QR-Code-Verifizierungslinks auf Lizenz-PDFs verwendet.
                </p>
            </CardHeader>
            <CardContent>
                {loading ? <div className="animate-pulse h-10 bg-muted rounded" /> : (
                    <div className="flex gap-3 items-end">
                        <div className="flex-1">
                            <Label htmlFor="live-url">URL der Live-App</Label>
                            <Input
                                id="live-url"
                                value={liveUrl}
                                onChange={(e) => setLiveUrl(e.target.value)}
                                placeholder="https://verwaltung.bgpr.org"
                                data-testid="live-url-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Beispiel: https://verwaltung.bgpr.org &mdash; QR-Code verweist dann auf: {liveUrl || 'https://...'}/verify.php?token=xxx
                            </p>
                        </div>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-live-url-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                            Speichern
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function AkademieUrlSettings() {
    const [url, setUrl] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        api.get('/settings/akademie-url')
            .then(res => setUrl(res.data.akademie_url || ''))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.post('/settings/akademie-url', { akademie_url: url });
            toast.success('BGPR-Akademie URL gespeichert');
        } catch { toast.error('Fehler beim Speichern'); }
        finally { setSaving(false); }
    };

    const previewUrl = url || window.location.origin;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <GraduationCap className="h-5 w-5" />
                    BGPR-Akademie (Öffentliche Seite)
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                    Die URL zur öffentlichen Seminar-Seite, über die sich Teilnehmer informieren und anmelden können.
                </p>
            </CardHeader>
            <CardContent>
                {loading ? <div className="animate-pulse h-10 bg-muted rounded" /> : (
                    <div className="space-y-3">
                        <div className="flex gap-3 items-end">
                            <div className="flex-1">
                                <Label htmlFor="akademie-url">URL der BGPR-Akademie</Label>
                                <Input
                                    id="akademie-url"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://akademie.bgpr.org"
                                    data-testid="akademie-url-input"
                                />
                            </div>
                            <Button onClick={handleSave} disabled={saving} data-testid="save-akademie-url-btn">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                                Speichern
                            </Button>
                        </div>
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                            <p className="text-blue-800">
                                <strong>Vorschau:</strong>{' '}
                                <a href={`${previewUrl}/seminare`} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">
                                    {previewUrl}/seminare
                                </a>
                            </p>
                            <p className="text-blue-700 text-xs mt-1">
                                Unter dieser Adresse können Teilnehmer das Seminarangebot einsehen, sich registrieren und zu Seminaren anmelden.
                            </p>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}



function LicenseWarningSettings() {
    const [days, setDays] = React.useState(90);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        settingsApi.getLicenseWarning()
            .then(res => setDays(res.data.days || 90))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await settingsApi.saveLicenseWarning({ days: parseInt(days) });
            toast.success('Warnzeitraum gespeichert');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Lizenz-Warnzeitraum
                </CardTitle>
                <CardDescription>
                    Legen Sie fest, wie viele Tage vor Ablauf einer Lizenz eine Warnung im Dashboard angezeigt wird.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="flex items-end gap-4 max-w-sm">
                    <div className="flex-1 space-y-2">
                        <Label htmlFor="warning-days">Warnzeitraum (Tage)</Label>
                        <Input
                            id="warning-days"
                            type="number"
                            min={1}
                            max={365}
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                            data-testid="license-warning-days"
                        />
                    </div>
                    <Button onClick={handleSave} disabled={saving} data-testid="save-warning-days">
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Speichern
                    </Button>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                    Aktuell werden Warnungen <strong>{days} Tage</strong> vor Ablauf angezeigt.
                </p>
            </CardContent>
        </Card>
    );
}


function AuditLogSettings() {
    const [events, setEvents] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [category, setCategory] = React.useState('all');

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await settingsApi.getAuditLogRecent(300, category);
            setEvents(res.data || []);
        } catch {
            toast.error('Audit-Log konnte nicht geladen werden');
        } finally {
            setLoading(false);
        }
    }, [category]);

    React.useEffect(() => { load(); }, [load]);

    const ACTION_MAP = {
        // Admin-Aktionen (admin_audit_log)
        impersonate:                { label: 'Einloggen als Nutzer',    cls: 'bg-amber-100 text-amber-800' },
        user_delete:                { label: 'Nutzer gelöscht',         cls: 'bg-red-100 text-red-800' },
        password_reset:             { label: 'Passwort zurückgesetzt',  cls: 'bg-blue-100 text-blue-800' },
        smtp_update:                { label: 'SMTP-Zugang geändert',    cls: 'bg-purple-100 text-purple-800' },
        notifications_update:       { label: 'Benachrichtigungen geändert', cls: 'bg-purple-50 text-purple-700' },
        email_signature_update:     { label: 'Mail-Signatur geändert',  cls: 'bg-purple-50 text-purple-700' },
        orphan_cleanup:             { label: 'Verwaiste Daten bereinigt', cls: 'bg-slate-100 text-slate-700' },
        account_unlock:             { label: 'Konto entsperrt (manuell)', cls: 'bg-emerald-100 text-emerald-800' },
        // Auth/Security (audit_logs)
        'auth.login.success':       { label: 'Login erfolgreich',       cls: 'bg-emerald-100 text-emerald-800' },
        'auth.login.fail':          { label: 'Login fehlgeschlagen',    cls: 'bg-red-100 text-red-800' },
        'auth.account.locked':      { label: 'Konto gesperrt (Brute-Force-Schutz)', cls: 'bg-red-100 text-red-900' },
        'security.rate_limited':    { label: 'Rate-Limit ausgelöst',    cls: 'bg-amber-100 text-amber-900' },
        'user.admin.created':       { label: 'Admin-Konto erstellt',    cls: 'bg-indigo-100 text-indigo-800' },
    };
    const fmtDate = (iso) => {
        if (!iso) return '–';
        try {
            const d = new Date(iso);
            return d.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
        } catch { return iso; }
    };

    const CategoryButton = ({ value, label, count }) => (
        <button
            type="button"
            onClick={() => setCategory(value)}
            data-testid={`audit-filter-${value}`}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                category === value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted'
            }`}
        >
            {label}{typeof count === 'number' ? ` (${count})` : ''}
        </button>
    );

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
                <div>
                    <CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5" /> Audit-Log</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                        Sicherheitsrelevante Ereignisse: Anmeldungen (Erfolg/Fehler) und kritische Admin-Aktionen
                        (Impersonation, SMTP-Änderungen, Passwort-Resets). Nur für Admins sichtbar.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-audit-log">
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Aktualisieren
                </Button>
            </CardHeader>
            <CardContent>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="text-xs text-muted-foreground mr-1">Filter:</span>
                    <CategoryButton value="all"   label="Alle" />
                    <CategoryButton value="auth"  label="Logins / Security" />
                    <CategoryButton value="admin" label="Admin-Aktionen" />
                </div>
                {loading ? (
                    <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : events.length === 0 ? (
                    <div className="py-16 text-center text-sm text-muted-foreground">Noch keine Einträge.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="audit-log-table">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                                    <th className="py-2 px-3">Zeitpunkt</th>
                                    <th className="py-2 px-3">Aktion</th>
                                    <th className="py-2 px-3">Akteur</th>
                                    <th className="py-2 px-3">Ziel-Nutzer</th>
                                    <th className="py-2 px-3">IP</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {events.map((e, idx) => {
                                    const info = ACTION_MAP[e.action] || { label: e.action, cls: 'bg-muted text-foreground' };
                                    const key = e.id || `${e.ts}-${idx}`;
                                    return (
                                        <tr key={key} className="hover:bg-muted/30" data-testid={`audit-row-${key}`}>
                                            <td className="py-2 px-3 font-mono text-xs whitespace-nowrap">{fmtDate(e.ts)}</td>
                                            <td className="py-2 px-3">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${info.cls}`}>
                                                    {info.label}
                                                </span>
                                            </td>
                                            <td className="py-2 px-3">{e.actor_email || <span className="text-muted-foreground italic">System/Anonym</span>}</td>
                                            <td className="py-2 px-3">
                                                {e.target_email ? (
                                                    <div className="leading-tight">
                                                        <div>{e.target_name || '–'}</div>
                                                        <div className="text-xs text-muted-foreground">{e.target_email}</div>
                                                    </div>
                                                ) : <span className="text-muted-foreground italic">–</span>}
                                            </td>
                                            <td className="py-2 px-3 font-mono text-xs text-muted-foreground">
                                                {e.ip || '–'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}


function EmailSignatureSettings() {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [sendingTest, setSendingTest] = React.useState(false);
    const [enabled, setEnabled] = React.useState(true);
    const [html, setHtml] = React.useState('');
    // Raw-Modus: externer HTML-Quellcode wird 1:1 eingefügt (kein Wrapper-<div>, kein Editor-Cleanup).
    // Sinnvoll für extern erzeugte Signaturen (Outlook, Signatur-Generator, Designer), bei denen
    // jedes zusätzliche Layout-Element die Optik zerstören würde.
    const [rawMode, setRawMode] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);
    const [testEmail, setTestEmail] = React.useState('');

    React.useEffect(() => {
        settingsApi.getEmailSignature().then(res => {
            setEnabled(res.data.enabled);
            setHtml(res.data.html || '');
            setRawMode(!!res.data.raw_mode);
            setLoaded(true);
        }).catch(() => {
            toast.error('Signatur konnte nicht geladen werden');
            setLoaded(true);
        }).finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await settingsApi.saveEmailSignature({ enabled, html, raw_mode: rawMode });
            toast.success('Mail-Signatur gespeichert');
        } catch {
            toast.error('Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        const target = testEmail.trim();
        if (!target) {
            toast.error('Bitte eine Empfänger-E-Mail angeben');
            return;
        }
        setSendingTest(true);
        try {
            await settingsApi.saveEmailSignature({ enabled, html, raw_mode: rawMode });
            const res = await settingsApi.testEmailSignature(target);
            toast.success(res.data.message || 'Test-Mail versendet');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Test-Versand fehlgeschlagen');
        } finally {
            setSendingTest(false);
        }
    };

    if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Mail-Signatur / Abbinder</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                    Wird an <strong>alle</strong> System-Mails automatisch unten angehängt (Anmeldebestätigung, Zahlungsaufforderung, Zahlungsbestätigung, Meeting-Links, Erinnerungen, etc.).
                </p>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="sig-enabled"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        className="h-4 w-4"
                        data-testid="signature-enabled"
                    />
                    <Label htmlFor="sig-enabled" className="cursor-pointer">Signatur aktiv an alle Mails anhängen</Label>
                </div>

                {/* Raw-Mode Toggle */}
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="signature-raw-mode-block">
                    <div className="flex items-start gap-2">
                        <input
                            type="checkbox"
                            id="sig-raw-mode"
                            checked={rawMode}
                            onChange={(e) => setRawMode(e.target.checked)}
                            className="h-4 w-4 mt-0.5"
                            data-testid="signature-raw-mode-toggle"
                        />
                        <div className="flex-1">
                            <Label htmlFor="sig-raw-mode" className="cursor-pointer font-semibold text-amber-900">
                                Externer HTML-Quellcode (1:1 unverändert übernehmen)
                            </Label>
                            <p className="text-xs text-amber-900/80 mt-1">
                                Aktiviere diese Option, wenn du einen fertigen HTML-Signatur-Quellcode hast –
                                z. B. von einem Signatur-Generator, Outlook-Export oder einer eigenen Vorlage.
                                Der Code wird dann <strong>unverändert</strong> in jede Mail eingefügt
                                (kein automatischer Trennstrich, keine Style-Anpassungen, kein Rich-Text-Editor).
                            </p>
                        </div>
                    </div>
                </div>

                {loaded && (
                    <div>
                        <Label className="mb-2 block">
                            {rawMode ? 'HTML-Quellcode der externen Signatur' : 'Inhalt der Signatur'}
                        </Label>
                        {rawMode ? (
                            <textarea
                                value={html}
                                onChange={(e) => setHtml(e.target.value)}
                                placeholder={'<!-- z. B. <table>...</table> aus deinem Signatur-Generator -->'}
                                spellCheck={false}
                                className="w-full min-h-[280px] font-mono text-xs p-3 border rounded-md bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                                data-testid="signature-raw-textarea"
                            />
                        ) : (
                            <EmailEditor
                                content={html}
                                onChange={setHtml}
                                showPlaceholders={false}
                            />
                        )}
                        {rawMode && (
                            <p className="text-[11px] text-muted-foreground mt-2">
                                Tipp: Externe Bilder (z. B. <code>&lt;img src="https://..."&gt;</code>) bitte als HTTPS einbinden.
                                CID-/Inline-Bilder werden nicht automatisch eingebettet.
                            </p>
                        )}
                    </div>
                )}
                <div className="flex flex-wrap items-end gap-3 justify-between border-t pt-4">
                    <div className="flex-1 min-w-[280px]">
                        <Label htmlFor="sig-test-email" className="text-xs text-muted-foreground mb-1 block">Test-Mail senden an</Label>
                        <div className="flex gap-2">
                            <Input
                                id="sig-test-email"
                                type="email"
                                placeholder="deine@adresse.de"
                                value={testEmail}
                                onChange={(e) => setTestEmail(e.target.value)}
                                data-testid="signature-test-email"
                            />
                            <Button variant="outline" onClick={handleTest} disabled={sendingTest} data-testid="send-test-mail-btn">
                                {sendingTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                                Test-Mail
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            Speichert und schickt eine Beispiel-Mail mit aktueller Signatur.
                        </p>
                    </div>
                    <Button onClick={handleSave} disabled={saving} data-testid="save-signature-btn">
                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Speichern
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}


function SmtpTestCard() {
    const [testEmail, setTestEmail] = React.useState('');
    const [testing, setTesting] = React.useState(false);
    const [result, setResult] = React.useState(null);

    const handleTest = async () => {
        if (!testEmail.trim()) {
            toast.error('Bitte geben Sie eine Empfänger-E-Mail ein');
            return;
        }
        setTesting(true);
        setResult(null);
        try {
            const res = await settingsApi.testSmtp(testEmail);
            setResult({ success: true, message: res.data.message });
            toast.success(res.data.message);
        } catch (error) {
            const msg = error.response?.data?.detail || 'Verbindung fehlgeschlagen';
            setResult({ success: false, message: msg });
            toast.error(msg);
        } finally {
            setTesting(false);
        }
    };

    return (
        <Card className="mt-4">
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-base">
                    <Send className="h-4 w-4" />
                    SMTP-Verbindung testen
                </CardTitle>
                <CardDescription>
                    Senden Sie eine Test-E-Mail, um die SMTP-Konfiguration zu prüfen. Bitte speichern Sie die Einstellungen zuerst.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="flex items-end gap-3 max-w-lg">
                    <div className="flex-1 space-y-2">
                        <Label htmlFor="test-email">Empfänger E-Mail</Label>
                        <Input
                            id="test-email"
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="ihre@email.de"
                            data-testid="smtp-test-email"
                        />
                    </div>
                    <Button
                        onClick={handleTest}
                        disabled={testing}
                        variant="outline"
                        data-testid="smtp-test-button"
                    >
                        {testing ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="mr-2 h-4 w-4" />
                        )}
                        Test senden
                    </Button>
                </div>
                {result && (
                    <div className={`mt-3 p-3 rounded-lg text-sm ${result.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="smtp-test-result">
                        {result.success ? '✓' : '✗'} {result.message}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}


function QualificationSettings() {
    const [options, setOptions] = React.useState([]);
    const [newOption, setNewOption] = React.useState('');
    const [editingIdx, setEditingIdx] = React.useState(null);
    const [editValue, setEditValue] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        settingsApi.getQualifications()
            .then(res => setOptions(res.data.options || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleAdd = () => {
        const trimmed = newOption.trim();
        if (trimmed && !options.includes(trimmed)) {
            setOptions([...options, trimmed]);
            setNewOption('');
        }
    };

    const handleRemove = (idx) => {
        setOptions(options.filter((_, i) => i !== idx));
        if (editingIdx === idx) setEditingIdx(null);
    };

    const handleMove = (idx, direction) => {
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= options.length) return;
        const copy = [...options];
        [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
        setOptions(copy);
        if (editingIdx === idx) setEditingIdx(newIdx);
        else if (editingIdx === newIdx) setEditingIdx(idx);
    };

    const startEdit = (idx) => {
        setEditingIdx(idx);
        setEditValue(options[idx]);
    };

    const commitEdit = () => {
        if (editingIdx === null) return;
        const trimmed = editValue.trim();
        if (trimmed && (trimmed === options[editingIdx] || !options.includes(trimmed))) {
            const copy = [...options];
            copy[editingIdx] = trimmed;
            setOptions(copy);
        }
        setEditingIdx(null);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await settingsApi.saveQualifications({ options });
            toast.success('Qualifikationen gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <Type className="h-5 w-5" />
                    Berufliche Vorqualifikationen
                </CardTitle>
                <CardDescription>
                    Verwalten Sie die Auswahlliste für Vorqualifikationen der Übungsleiter.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    {options.map((opt, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2.5 bg-muted/50 rounded-lg group">
                            {editingIdx === idx ? (
                                <Input
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={commitEdit}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitEdit(); } if (e.key === 'Escape') setEditingIdx(null); }}
                                    autoFocus
                                    className="h-8 text-sm flex-1 mr-2"
                                    data-testid={`edit-qual-input-${idx}`}
                                />
                            ) : (
                                <span
                                    className="text-sm font-medium cursor-pointer hover:text-primary"
                                    onDoubleClick={() => startEdit(idx)}
                                    data-testid={`qual-text-${idx}`}
                                >
                                    {opt}
                                </span>
                            )}
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => startEdit(idx)}
                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                                    data-testid={`edit-qual-${idx}`}
                                    title="Bearbeiten"
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleMove(idx, -1)}
                                    disabled={idx === 0}
                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground disabled:opacity-30"
                                    data-testid={`move-up-qual-${idx}`}
                                >
                                    <ChevronUp className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleMove(idx, 1)}
                                    disabled={idx === options.length - 1}
                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground disabled:opacity-30"
                                    data-testid={`move-down-qual-${idx}`}
                                >
                                    <ChevronDown className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemove(idx)}
                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600"
                                    data-testid={`remove-qual-${idx}`}
                                >
                                    <XCircle className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                    {options.length === 0 && (
                        <p className="text-sm text-muted-foreground">Keine Qualifikationen definiert</p>
                    )}
                </div>

                <div className="flex gap-2">
                    <Input
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
                        placeholder="Neue Qualifikation hinzufügen..."
                        className="flex-1"
                        data-testid="new-qualification-input"
                    />
                    <Button variant="outline" onClick={handleAdd} data-testid="add-qualification-button">
                        Hinzufügen
                    </Button>
                </div>

                <Button onClick={handleSave} disabled={saving} data-testid="save-qualifications-button">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Speichern
                </Button>
            </CardContent>
        </Card>
    );
}



function LicenseTypeSettings() {
    const [items, setItems] = React.useState([]);
    const [templates, setTemplates] = React.useState([]);
    const [newName, setNewName] = React.useState('');
    const [newTpl, setNewTpl] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [savingId, setSavingId] = React.useState(null);
    const [showArchived, setShowArchived] = React.useState(false);

    const load = React.useCallback(() => {
        Promise.all([
            settingsApi.getLicenseTypes(),
            templateApi.getAll(),
        ]).then(([lt, tpl]) => {
            setItems(lt.data.items || []);
            // Strikt: nur Templates mit type === 'license'. Andere sind hier nicht gültig
            // (Zertifikate/Rechnungen/etc.), sonst gab es zuvor mehrdeutige Zuordnungen,
            // bei denen mehrere Vorlagen denselben `type`-Wert teilten.
            const list = tpl.data || [];
            const licTpl = list.filter(t => String(t.type || '').toLowerCase() === 'license');
            setTemplates(licTpl);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        const trimmed = newName.trim();
        if (!trimmed) return;
        try {
            await settingsApi.createLicenseType({ name: trimmed, template_id: newTpl || null });
            setNewName(''); setNewTpl('');
            toast.success('Lizenzart hinzugefügt');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        }
    };

    const handleUpdate = async (id, patch, opts = {}) => {
        setSavingId(id);
        try {
            await settingsApi.updateLicenseType(id, patch);
            if (opts.silent !== true) toast.success('Gespeichert');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        } finally {
            setSavingId(null);
        }
    };

    if (loading) return (
        <Card><CardContent className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>
    );

    const visible = items.filter(i => showArchived ? true : !i.archived);
    // Anzeige-Namen der Vorlage über die eindeutige ID auflösen
    const findTplById = (tid) => templates.find(t => t.id === tid);
    const templateDisplayForItem = (item) => {
        if (item.template_id) {
            const t = findTplById(item.template_id);
            return t ? t.name : '(Vorlage gelöscht)';
        }
        if (item.template_type) return `Legacy: ${item.template_type}`;
        return null;
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <Award className="h-5 w-5" /> Lizenzarten
                </CardTitle>
                <CardDescription>
                    Namen der Lizenzen + zugehörige PDF-Vorlage. Änderungen an der Vorlagen-Auswahl werden sofort gespeichert. Bei Ausbildungen und manueller Lizenz-Ausstellung stehen alle nicht-archivierten Einträge zur Auswahl.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{items.filter(i => !i.archived).length} aktiv · {items.filter(i => i.archived).length} archiviert · {templates.length} Lizenz-Vorlagen verfügbar</span>
                    <button type="button" onClick={() => setShowArchived(v => !v)} className="underline hover:text-foreground" data-testid="toggle-archived-lictypes">
                        {showArchived ? 'Archivierte ausblenden' : 'Archivierte anzeigen'}
                    </button>
                </div>

                <div className="space-y-2" data-testid="license-types-list">
                    {visible.map((item) => (
                        <div key={item.id} className={`p-3 rounded-lg border ${item.archived ? 'bg-muted/40 opacity-70' : 'bg-card'}`} data-testid={`lictype-row-${item.id}`}>
                            <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] items-center">
                                <Input
                                    defaultValue={item.name}
                                    onBlur={(e) => {
                                        const v = e.target.value.trim();
                                        if (v && v !== item.name) handleUpdate(item.id, { name: v });
                                    }}
                                    disabled={item.archived}
                                    data-testid={`lictype-name-${item.id}`}
                                />
                                <Select
                                    value={item.template_id || '__none__'}
                                    onValueChange={(v) => handleUpdate(item.id, { template_id: v === '__none__' ? null : v })}
                                    disabled={item.archived}
                                >
                                    <SelectTrigger data-testid={`lictype-template-${item.id}`}>
                                        <SelectValue placeholder="— Keine PDF-Vorlage —" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— Keine PDF-Vorlage —</SelectItem>
                                        {templates.map((t) => (
                                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <div className="flex items-center gap-1">
                                    {savingId === item.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleUpdate(item.id, { archived: !item.archived })}
                                        className={item.archived ? 'text-emerald-700' : 'text-amber-700'}
                                        data-testid={`lictype-archive-${item.id}`}
                                        title={item.archived ? 'Reaktivieren' : 'Archivieren'}
                                    >
                                        {item.archived ? 'Reaktivieren' : 'Archivieren'}
                                    </Button>
                                </div>
                            </div>
                            {templateDisplayForItem(item) && (
                                <p className="text-[10px] text-muted-foreground mt-1">Verknüpfte Vorlage: <span className="font-medium">{templateDisplayForItem(item)}</span></p>
                            )}
                            {!item.template_id && !item.template_type && !item.archived && (
                                <p className="text-[10px] text-amber-700 mt-1">⚠ Keine PDF-Vorlage verknüpft — beim Ausstellen wird die Standard-Lizenz-Vorlage genutzt.</p>
                            )}
                        </div>
                    ))}
                    {visible.length === 0 && (
                        <p className="text-sm text-muted-foreground">Keine {showArchived ? '' : 'aktiven '}Lizenzarten.</p>
                    )}
                </div>

                <div className="border-t pt-3 space-y-2">
                    <Label className="text-sm font-semibold">Neue Lizenzart</Label>
                    <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                        <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="z. B. DGPR-Herzgruppenleiter Innere Medizin"
                            data-testid="new-license-type-input"
                        />
                        <Select value={newTpl} onValueChange={setNewTpl}>
                            <SelectTrigger data-testid="new-license-type-template"><SelectValue placeholder="PDF-Vorlage wählen (optional)" /></SelectTrigger>
                            <SelectContent>
                                {templates.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={handleAdd} data-testid="add-license-type-button">
                            Hinzufügen
                        </Button>
                    </div>
                    {templates.length === 0 && (
                        <p className="text-xs text-amber-700">Keine Lizenz-Vorlagen gefunden — bitte zuerst unter Einstellungen → PDF-Vorlagen eine Vorlage vom Typ „Lizenz" anlegen.</p>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}


// ============ Ablehnungsgründe (Fortbildungsnachweise) ============
function RejectionReasonsSettings() {
    const [options, setOptions] = React.useState([]);
    const [newOption, setNewOption] = React.useState('');
    const [editingIdx, setEditingIdx] = React.useState(null);
    const [editValue, setEditValue] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        settingsApi.getRejectionReasons()
            .then((res) => setOptions(res.data.options || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleAdd = () => {
        const trimmed = newOption.trim();
        if (trimmed && !options.includes(trimmed)) {
            setOptions([...options, trimmed]);
            setNewOption('');
        }
    };
    const handleRemove = (idx) => {
        setOptions(options.filter((_, i) => i !== idx));
        if (editingIdx === idx) setEditingIdx(null);
    };
    const handleMove = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= options.length) return;
        const copy = [...options];
        [copy[idx], copy[target]] = [copy[target], copy[idx]];
        setOptions(copy);
    };
    const startEdit = (idx) => { setEditingIdx(idx); setEditValue(options[idx]); };
    const commitEdit = () => {
        if (editingIdx === null) return;
        const trimmed = editValue.trim();
        if (trimmed && (trimmed === options[editingIdx] || !options.includes(trimmed))) {
            const copy = [...options];
            copy[editingIdx] = trimmed;
            setOptions(copy);
        }
        setEditingIdx(null);
    };
    const handleSave = async () => {
        setSaving(true);
        try {
            await settingsApi.saveRejectionReasons({ options });
            toast.success('Ablehnungsgründe gespeichert');
        } catch {
            toast.error('Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Card><CardContent className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>;
    }
    return (
        <Card data-testid="rejection-reasons-settings">
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <XCircle className="h-5 w-5" />
                    Ablehnungsgründe (Fortbildungsnachweise)
                </CardTitle>
                <CardDescription>
                    Auswahlliste für die Ablehnung extern hochgeladener Fortbildungsnachweise.
                    Der Grund erscheint in der E-Mail an das Mitglied und in der Portal-Ansicht.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    {options.map((opt, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2.5 bg-muted/50 rounded-lg">
                            {editingIdx === idx ? (
                                <Input
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={commitEdit}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitEdit(); } if (e.key === 'Escape') setEditingIdx(null); }}
                                    autoFocus
                                    className="h-8 text-sm flex-1 mr-2"
                                    data-testid={`edit-reject-input-${idx}`}
                                />
                            ) : (
                                <span className="text-sm font-medium cursor-pointer hover:text-primary" onDoubleClick={() => startEdit(idx)} data-testid={`reject-text-${idx}`}>
                                    {opt}
                                </span>
                            )}
                            <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => startEdit(idx)} className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" data-testid={`edit-reject-${idx}`} title="Bearbeiten">
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleMove(idx, -1)} disabled={idx === 0} className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`move-up-reject-${idx}`}>
                                    <ChevronUp className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleMove(idx, 1)} disabled={idx === options.length - 1} className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`move-down-reject-${idx}`}>
                                    <ChevronDown className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleRemove(idx)} className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" data-testid={`remove-reject-${idx}`}>
                                    <XCircle className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                    {options.length === 0 && (
                        <p className="text-sm text-muted-foreground">Keine Ablehnungsgründe definiert</p>
                    )}
                </div>
                <div className="flex gap-2">
                    <Input
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
                        placeholder="Neuen Ablehnungsgrund hinzufügen..."
                        className="flex-1"
                        data-testid="new-reject-input"
                    />
                    <Button variant="outline" onClick={handleAdd} data-testid="add-reject-button">
                        Hinzufügen
                    </Button>
                </div>
                <Button onClick={handleSave} disabled={saving} data-testid="save-reject-button">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Speichern
                </Button>
            </CardContent>
        </Card>
    );
}


function LegalTextsSettings() {
    const [termsContent, setTermsContent] = useState('');
    const [privacyContent, setPrivacyContent] = useState('');
    const [imprintContent, setImprintContent] = useState('');
    const [termsLoaded, setTermsLoaded] = useState(false);
    const [privacyLoaded, setPrivacyLoaded] = useState(false);
    const [imprintLoaded, setImprintLoaded] = useState(false);
    const [savingTerms, setSavingTerms] = useState(false);
    const [savingPrivacy, setSavingPrivacy] = useState(false);
    const [savingImprint, setSavingImprint] = useState(false);

    useEffect(() => {
        loadTexts();
    }, []);

    const loadTexts = async () => {
        try {
            const [termsRes, privacyRes, imprintRes] = await Promise.all([
                legalApi.get('terms_of_service'),
                legalApi.get('privacy_policy'),
                legalApi.get('imprint'),
            ]);
            setTermsContent(termsRes.data.html_content || '');
            setPrivacyContent(privacyRes.data.html_content || '');
            setImprintContent(imprintRes.data.html_content || '');
        } catch (error) {
            console.error('Fehler beim Laden der Rechtstexte:', error);
        } finally {
            setTermsLoaded(true);
            setPrivacyLoaded(true);
            setImprintLoaded(true);
        }
    };

    const handleSaveTerms = async () => {
        setSavingTerms(true);
        try {
            await legalApi.update('terms_of_service', termsContent);
            toast.success('Nutzungsbedingungen gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setSavingTerms(false);
        }
    };

    const handleSavePrivacy = async () => {
        setSavingPrivacy(true);
        try {
            await legalApi.update('privacy_policy', privacyContent);
            toast.success('Datenschutzhinweise gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setSavingPrivacy(false);
        }
    };

    const handleSaveImprint = async () => {
        setSavingImprint(true);
        try {
            await legalApi.update('imprint', imprintContent);
            toast.success('Impressum gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setSavingImprint(false);
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Nutzungsbedingungen
                    </CardTitle>
                    <CardDescription>
                        Diese Nutzungsbedingungen werden bei der Registrierung angezeigt und müssen von Teilnehmern akzeptiert werden.
                        Erreichbar unter <code className="text-xs bg-muted px-1 py-0.5 rounded">/nutzungsbedingungen</code>
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {termsLoaded && (
                            <EmailEditor
                                content={termsContent}
                                onChange={setTermsContent}
                                showPlaceholders={false}
                            />
                        )}
                        <Button onClick={handleSaveTerms} disabled={savingTerms} data-testid="save-terms-button">
                            {savingTerms ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                            Nutzungsbedingungen speichern
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Datenschutzhinweise
                    </CardTitle>
                    <CardDescription>
                        Diese Datenschutzhinweise werden bei der Registrierung angezeigt und müssen von Teilnehmern akzeptiert werden.
                        Erreichbar unter <code className="text-xs bg-muted px-1 py-0.5 rounded">/datenschutz</code>
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {privacyLoaded && (
                            <EmailEditor
                                content={privacyContent}
                                onChange={setPrivacyContent}
                                showPlaceholders={false}
                            />
                        )}
                        <Button onClick={handleSavePrivacy} disabled={savingPrivacy} data-testid="save-privacy-button">
                            {savingPrivacy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                            Datenschutzhinweise speichern
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Impressum
                    </CardTitle>
                    <CardDescription>
                        Pflicht-Angaben gemäß § 5 TMG. Wird im Footer und unter
                        <code className="text-xs bg-muted px-1 py-0.5 rounded ml-1">/impressum</code> angezeigt.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {imprintLoaded && (
                            <EmailEditor
                                content={imprintContent}
                                onChange={setImprintContent}
                                showPlaceholders={false}
                            />
                        )}
                        <Button onClick={handleSaveImprint} disabled={savingImprint} data-testid="save-imprint-button">
                            {savingImprint ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                            Impressum speichern
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function DropboxSettings() {
    const [appKey, setAppKey] = useState('');
    const [appSecret, setAppSecret] = useState('');
    const [appSecretMasked, setAppSecretMasked] = useState('');
    const [baseFolder, setBaseFolder] = useState('/BGPR');
    const [maskedToken, setMaskedToken] = useState('');
    const [hasRefreshToken, setHasRefreshToken] = useState(false);
    const [accountName, setAccountName] = useState('');
    const [accountEmail, setAccountEmail] = useState('');
    const [connectedAt, setConnectedAt] = useState('');
    const [refreshedAt, setRefreshedAt] = useState('');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [connectionResult, setConnectionResult] = useState(null);
    const [folders, setFolders] = useState([]);
    const [currentPath, setCurrentPath] = useState('');
    const [browsing, setBrowsing] = useState(false);
    const [authUrl, setAuthUrl] = useState('');
    const [authCode, setAuthCode] = useState('');
    const [authLoading, setAuthLoading] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const res = await dropboxApi.getSettings();
            setAppKey(res.data.app_key || '');
            setAppSecretMasked(res.data.app_secret_masked || '');
            setBaseFolder(res.data.base_folder || '/BGPR');
            setMaskedToken(res.data.access_token_masked || '');
            setHasRefreshToken(!!res.data.has_refresh_token);
            setAccountName(res.data.account_name || '');
            setAccountEmail(res.data.account_email || '');
            setConnectedAt(res.data.connected_at || '');
            setRefreshedAt(res.data.access_token_refreshed_at || '');
        } catch (error) {
            console.error('Fehler:', error);
        }
    };

    const handleSaveApp = async () => {
        setSaving(true);
        try {
            const payload = { app_key: appKey, base_folder: baseFolder };
            if (appSecret) payload.app_secret = appSecret;
            await dropboxApi.saveSettings(payload);
            toast.success('Einstellungen gespeichert');
            setAppSecret('');
            loadSettings();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleStartAuth = async () => {
        setAuthLoading(true);
        try {
            const res = await dropboxApi.authStart();
            setAuthUrl(res.data.auth_url);
            window.open(res.data.auth_url, '_blank', 'noopener');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Autorisierung konnte nicht gestartet werden');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleFinishAuth = async () => {
        if (!authCode.trim()) {
            toast.error('Bitte Code einfügen');
            return;
        }
        setAuthLoading(true);
        try {
            const res = await dropboxApi.authFinish(authCode.trim());
            toast.success(`Dropbox verbunden: ${res.data.account_name || ''}`);
            setAuthCode('');
            setAuthUrl('');
            loadSettings();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Verbindung fehlgeschlagen');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Dropbox-Verbindung wirklich trennen? Die App-Konfiguration bleibt erhalten.')) return;
        try {
            await dropboxApi.disconnect();
            toast.success('Dropbox getrennt');
            loadSettings();
        } catch (error) {
            toast.error('Fehler beim Trennen');
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setConnectionResult(null);
        try {
            const res = await dropboxApi.testConnection();
            setConnectionResult({ success: true, ...res.data });
            loadSettings();
        } catch (error) {
            setConnectionResult({ success: false, error: error.response?.data?.detail || 'Verbindung fehlgeschlagen' });
        } finally {
            setTesting(false);
        }
    };

    const handleBrowse = async (path) => {
        setBrowsing(true);
        try {
            const res = await dropboxApi.listFolders(path);
            setFolders(res.data.entries || []);
            setCurrentPath(path || '');
        } catch (error) {
            toast.error('Fehler beim Laden der Ordner');
        } finally {
            setBrowsing(false);
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <HardDrive className="h-5 w-5" />
                        Dropbox-Verbindung
                        {hasRefreshToken && (
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full" data-testid="dropbox-connected-badge">Verbunden</span>
                        )}
                    </CardTitle>
                    <CardDescription>
                        Dauerhaft per OAuth-Refresh-Token verbunden &ndash; der Zugriff wird automatisch erneuert und läuft nicht mehr ab.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-6">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 space-y-2">
                            <p className="font-semibold">Einrichtung in 3 Schritten:</p>
                            <ol className="list-decimal list-inside space-y-1 text-blue-800 text-xs leading-relaxed">
                                <li>Öffnen Sie <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="underline font-medium">dropbox.com/developers/apps</a>, erstellen Sie eine <strong>Scoped Access</strong>-App mit <strong>Full Dropbox</strong>-Zugriff. Unter <strong>Permissions</strong> aktivieren: <code className="bg-blue-100 px-1 rounded">files.content.write</code>, <code className="bg-blue-100 px-1 rounded">files.content.read</code> &rarr; <strong>Submit</strong>.</li>
                                <li>Kopieren Sie <strong>App key</strong> und <strong>App secret</strong> aus der App-Übersicht und speichern Sie diese hier unten.</li>
                                <li>Klicken Sie auf <strong>Dropbox verbinden</strong>, autorisieren Sie die App im Popup-Fenster, kopieren Sie den angezeigten Code und fügen Sie ihn hier ein.</li>
                            </ol>
                            <p className="text-xs text-blue-600 mt-1">Der gespeicherte Refresh-Token erneuert den Zugriff automatisch &ndash; kein Ablauf mehr.</p>
                        </div>

                        {/* Step 1: App credentials */}
                        <div className="space-y-3 border rounded-lg p-4">
                            <h3 className="font-semibold text-sm">1. App-Konfiguration</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <Label htmlFor="dbx_app_key" className="text-xs">App key</Label>
                                    <Input id="dbx_app_key" value={appKey} onChange={(e) => setAppKey(e.target.value)} placeholder="xxxxxxxxxxxxxxxx" data-testid="dropbox-app-key" />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="dbx_app_secret" className="text-xs">App secret</Label>
                                    <Input id="dbx_app_secret" type="password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} placeholder={appSecretMasked || 'xxxxxxxxxxxxxxxx'} data-testid="dropbox-app-secret" />
                                    {appSecretMasked && <p className="text-xs text-muted-foreground">Gespeichert: {appSecretMasked}</p>}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="dbx_folder" className="text-xs">Basis-Ordner</Label>
                                <Input id="dbx_folder" value={baseFolder} onChange={(e) => setBaseFolder(e.target.value)} placeholder="/BGPR" data-testid="dropbox-base-folder" />
                            </div>
                            <Button onClick={handleSaveApp} disabled={saving} data-testid="save-dropbox-settings">
                                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                App-Konfiguration speichern
                            </Button>
                        </div>

                        {/* Step 2: OAuth */}
                        <div className="space-y-3 border rounded-lg p-4">
                            <h3 className="font-semibold text-sm">2. Verbindung</h3>
                            {hasRefreshToken ? (
                                <div className="space-y-2">
                                    <div className="p-3 bg-green-50 border border-green-200 rounded text-sm" data-testid="dropbox-connection-info">
                                        <div className="font-medium text-green-900">{accountName || 'Verbunden'}</div>
                                        {accountEmail && <div className="text-xs text-green-700">{accountEmail}</div>}
                                        {connectedAt && <div className="text-xs text-green-700">Verbunden seit: {new Date(connectedAt).toLocaleString('de-DE')}</div>}
                                        {refreshedAt && <div className="text-xs text-green-600">Token zuletzt erneuert: {new Date(refreshedAt).toLocaleString('de-DE')}</div>}
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="test-dropbox">
                                            {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                            Verbindung testen
                                        </Button>
                                        <Button variant="destructive" onClick={handleDisconnect} data-testid="disconnect-dropbox">
                                            Verbindung trennen
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <Button onClick={handleStartAuth} disabled={authLoading || !appKey} data-testid="connect-dropbox">
                                        {authLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                        Dropbox verbinden
                                    </Button>
                                    {authUrl && (
                                        <div className="space-y-2 p-3 bg-amber-50 border border-amber-200 rounded">
                                            <p className="text-xs text-amber-900">
                                                Autorisieren Sie die App im neuen Fenster und fügen Sie den angezeigten Code hier ein:
                                                {' '}<a href={authUrl} target="_blank" rel="noopener noreferrer" className="underline">Fenster erneut öffnen</a>
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <Input value={authCode} onChange={(e) => setAuthCode(e.target.value)} placeholder="Autorisierungs-Code einfügen" data-testid="dropbox-auth-code" />
                                                <Button onClick={handleFinishAuth} disabled={authLoading || !authCode} data-testid="finish-dropbox-auth">
                                                    {authLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                                    Bestätigen
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {connectionResult && (
                            <div className={`p-4 rounded-lg border ${connectionResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="dropbox-test-result">
                                {connectionResult.success ? (
                                    <div>
                                        <p className="text-sm font-medium text-green-800">Verbindung erfolgreich</p>
                                        <p className="text-xs text-green-600 mt-1">Konto: {connectionResult.account_name} ({connectionResult.email})</p>
                                    </div>
                                ) : (
                                    <p className="text-sm font-medium text-red-800">{connectionResult.error}</p>
                                )}
                            </div>
                        )}
                        {!hasRefreshToken && maskedToken && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                                Ein alter, manueller Access-Token ist noch gespeichert ({maskedToken}) &ndash; dieser läuft ab. Bitte verbinden Sie neu über den OAuth-Flow oben.
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-base">Ordner-Browser</CardTitle>
                    <CardDescription>Durchsuchen Sie Ihre Dropbox-Ordner</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleBrowse('')} disabled={browsing} data-testid="browse-root">
                                Stammordner laden
                            </Button>
                            {currentPath && (
                                <Button variant="ghost" size="sm" onClick={() => {
                                    const parent = currentPath.split('/').slice(0, -1).join('/');
                                    handleBrowse(parent);
                                }}>
                                    Zurück
                                </Button>
                            )}
                            <span className="text-xs text-muted-foreground">{currentPath || '/'}</span>
                        </div>
                        {browsing && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
                        {folders.length > 0 && (
                            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                                {folders.map((f, i) => (
                                    <div
                                        key={i}
                                        className={`flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 ${!f.is_file ? 'cursor-pointer' : ''}`}
                                        onClick={() => !f.is_file && handleBrowse(f.path)}
                                    >
                                        <div className="flex items-center gap-2">
                                            {f.is_file ? (
                                                <FileText className="h-4 w-4 text-gray-400" />
                                            ) : (
                                                <FolderOpen className="h-4 w-4 text-amber-500" />
                                            )}
                                            <span className={f.is_file ? 'text-gray-600' : 'text-gray-900 font-medium'}>{f.name}</span>
                                        </div>
                                        {f.size && <span className="text-xs text-gray-400">{(f.size / 1024).toFixed(1)} KB</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                        {folders.length === 0 && !browsing && currentPath !== '' && (
                            <p className="text-sm text-muted-foreground">Ordner ist leer</p>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}


function RenewalRulesSettings() {
    const [rules, setRules] = useState([
        { label: 'Bis 3 Monate abgelaufen', max_expired_months: 3, required_ue: 15, renewal_years: 2 },
        { label: '4-12 Monate abgelaufen', max_expired_months: 12, required_ue: 30, renewal_years: 2 },
        { label: '13-24 Monate abgelaufen', max_expired_months: 24, required_ue: 45, renewal_years: 2 },
    ]);
    const [maxRenewableYears, setMaxRenewableYears] = useState(36);
    const [defaultRenewalYears, setDefaultRenewalYears] = useState(2);
    const [halfUeOneYear, setHalfUeOneYear] = useState(true);
    const [firstAidMaxAge, setFirstAidMaxAge] = useState(2);
    const [saving, setSaving] = useState(false);

    useEffect(() => { loadRules(); }, []);

    const loadRules = async () => {
        try {
            const res = await settingsApi.getRenewalRules();
            const d = res.data;
            if (d.rules) setRules(d.rules);
            if (d.max_renewable_months != null) setMaxRenewableYears(d.max_renewable_months);
            else if (d.max_renewable_years != null) setMaxRenewableYears(d.max_renewable_years * 12);
            if (d.default_renewal_years != null) setDefaultRenewalYears(d.default_renewal_years);
            if (d.half_ue_one_year_extension != null) setHalfUeOneYear(d.half_ue_one_year_extension);
            if (d.first_aid_max_age_years != null) setFirstAidMaxAge(d.first_aid_max_age_years);
        } catch (e) { console.error(e); }
    };

    const updateRule = (idx, field, value) => {
        const updated = rules.map((r, i) => i === idx ? { ...r, [field]: value } : r);
        setRules(updated);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await settingsApi.saveRenewalRules({
                rules,
                max_renewable_months: maxRenewableYears,
                default_renewal_years: defaultRenewalYears,
                half_ue_one_year_extension: halfUeOneYear,
                first_aid_max_age_years: firstAidMaxAge,
            });
            toast.success('Verlängerungsregeln gespeichert');
        } catch (e) { toast.error('Fehler beim Speichern'); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading">UE-Anforderungen nach Lizenzstatus</CardTitle>
                    <CardDescription>Legen Sie fest, wie viele Fortbildungs-UE je nach Ablaufzeitraum der Lizenz nachgewiesen werden müssen.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {rules.map((rule, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-3 items-end p-3 bg-gray-50 rounded-lg border" data-testid={`rule-row-${idx}`}>
                                <div className="col-span-5 space-y-1">
                                    <Label className="text-xs">Bezeichnung</Label>
                                    <Input value={rule.label} onChange={e => updateRule(idx, 'label', e.target.value)} className="h-9 text-sm" />
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <Label className="text-xs">Max. Monate abgelaufen</Label>
                                    <Input type="number" min="0" step="1" value={rule.max_expired_months} onChange={e => updateRule(idx, 'max_expired_months', parseInt(e.target.value) || 0)} className="h-9 text-sm" />
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <Label className="text-xs">Erforderliche UE</Label>
                                    <Input type="number" min="0" value={rule.required_ue} onChange={e => updateRule(idx, 'required_ue', parseInt(e.target.value) || 0)} className="h-9 text-sm" data-testid={`rule-ue-${idx}`} />
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <Label className="text-xs">Verlängerung (Jahre)</Label>
                                    <Input type="number" min="1" max="5" value={rule.renewal_years} onChange={e => updateRule(idx, 'renewal_years', parseInt(e.target.value) || 2)} className="h-9 text-sm" />
                                </div>
                                <div className="col-span-1 flex justify-end">
                                    <Button variant="ghost" size="sm" className="text-red-500 h-9" onClick={() => setRules(rules.filter((_, i) => i !== idx))}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={() => setRules([...rules, { label: '', max_expired_months: 0, required_ue: 0, renewal_years: 2 }])}>
                            <Plus className="h-4 w-4 mr-1" /> Stufe hinzufügen
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="font-heading">Allgemeine Regeln</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Max. Monate nach Ablauf (nicht mehr verlängerbar)</Label>
                            <Input type="number" min="1" value={maxRenewableYears} onChange={e => setMaxRenewableYears(parseInt(e.target.value) || 36)} data-testid="max-renewable-months" />
                        </div>
                        <div className="space-y-2">
                            <Label>Standard-Verlängerung (Jahre, zum 31.12.)</Label>
                            <Input type="number" min="1" max="5" value={defaultRenewalYears} onChange={e => setDefaultRenewalYears(parseInt(e.target.value) || 2)} data-testid="default-renewal-years" />
                        </div>
                        <div className="space-y-2">
                            <Label>1. Hilfe-Nachweis max. Alter (Jahre)</Label>
                            <Input type="number" min="1" value={firstAidMaxAge} onChange={e => setFirstAidMaxAge(parseInt(e.target.value) || 2)} data-testid="first-aid-max-age" />
                        </div>
                        <div className="flex items-center gap-3 pt-6">
                            <Switch checked={halfUeOneYear} onCheckedChange={setHalfUeOneYear} data-testid="half-ue-switch" />
                            <Label>Bei halber UE-Zahl nur 1 Jahr verlängern</Label>
                        </div>
                    </div>
                    <Button className="mt-6" onClick={handleSave} disabled={saving} data-testid="save-renewal-rules">
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Verlängerungsregeln speichern
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}



function LogoSettings() {
    const [logos, setLogos] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [uploading, setUploading] = React.useState({});
    const fileRefs = React.useRef({});

    const LOGO_TYPES = [
        { key: 'header', label: 'Header / Navigation', desc: 'Wird im Header der Admin-Seite und der öffentlichen Seite angezeigt (ideal: quadratisch oder quer, ca. 40px Höhe)' },
        { key: 'lists', label: 'Listen & Teilnehmerübersichten', desc: 'Wird auf Teilnehmerlisten und Anwesenheitslisten gedruckt' },
        { key: 'invoices', label: 'Rechnungen', desc: 'Wird auf allen Rechnungs-PDFs eingebunden' },
        { key: 'certificates', label: 'Zertifikate & Urkunden', desc: 'Wird auf Lehrgangsbescheinigungen und Lizenzen gedruckt' },
    ];

    React.useEffect(() => {
        loadLogos();
    }, []);

    const loadLogos = async () => {
        try {
            const res = await settingsApi.getLogos();
            setLogos(res.data || {});
        } catch (e) {
            console.error('Logos laden fehlgeschlagen', e);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (type, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const validExt = ['.png', '.jpg', '.jpeg', '.svg', '.webp'];
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!validExt.includes(ext)) {
            toast.error(`Ungültiges Format. Erlaubt: ${validExt.join(', ')}`);
            return;
        }
        setUploading(prev => ({ ...prev, [type]: true }));
        try {
            await settingsApi.uploadLogo(type, file);
            toast.success('Logo gespeichert');
            loadLogos();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Hochladen');
        } finally {
            setUploading(prev => ({ ...prev, [type]: false }));
            if (fileRefs.current[type]) fileRefs.current[type].value = '';
        }
    };

    const handleDelete = async (type) => {
        if (!window.confirm('Logo wirklich löschen?')) return;
        try {
            await settingsApi.deleteLogo(type);
            toast.success('Logo gelöscht');
            loadLogos();
        } catch {
            toast.error('Fehler beim Löschen');
        }
    };

    if (loading) {
        return <Card><CardContent className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>;
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Logos für PDF-Dokumente
                    </CardTitle>
                    <CardDescription>
                        Laden Sie Ihre Logos für die verschiedenen Dokumenttypen hoch. Empfohlen: PNG mit transparentem Hintergrund, mindestens 600px breit.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-6">
                        {LOGO_TYPES.map(({ key, label, desc }) => {
                            const logo = logos[key];
                            return (
                                <div key={key} className="flex items-start gap-6 p-4 bg-muted/30 rounded-lg border" data-testid={`logo-${key}`}>
                                    {/* Preview */}
                                    <div className="w-48 h-24 flex items-center justify-center bg-white border rounded-lg shrink-0 overflow-hidden">
                                        {logo ? (
                                            <img
                                                src={`${BACKEND_URL}/api/settings/logos/${key}?t=${Date.now()}`}
                                                alt={label}
                                                className="max-w-full max-h-full object-contain p-2"
                                                data-testid={`logo-preview-${key}`}
                                            />
                                        ) : (
                                            <span className="text-xs text-muted-foreground">Kein Logo</span>
                                        )}
                                    </div>
                                    {/* Info + Actions */}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm">{label}</p>
                                        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                                        {logo && (
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {logo.filename} · {(logo.size / 1024).toFixed(1)} KB
                                            </p>
                                        )}
                                        <div className="flex items-center gap-2 mt-3">
                                            <input
                                                type="file"
                                                accept=".png,.jpg,.jpeg,.svg,.webp"
                                                className="hidden"
                                                ref={(el) => { fileRefs.current[key] = el; }}
                                                onChange={(e) => handleUpload(key, e)}
                                            />
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => fileRefs.current[key]?.click()}
                                                disabled={uploading[key]}
                                                data-testid={`upload-logo-${key}`}
                                            >
                                                {uploading[key] ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                                                {logo ? 'Ersetzen' : 'Hochladen'}
                                            </Button>
                                            {logo && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-red-600 hover:bg-red-50"
                                                    onClick={() => handleDelete(key)}
                                                    data-testid={`delete-logo-${key}`}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                                                    Löschen
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}


function LpField({ label, desc, value, onChange, placeholder, multiline = false, testid }) {
    return (
        <div className="space-y-1">
            <Label className="text-sm font-medium">{label}</Label>
            {multiline ? (
                <Textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    placeholder={placeholder}
                    data-testid={testid}
                />
            ) : (
                <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={testid}
                />
            )}
            {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
        </div>
    );
}


function LoginPageSettings() {
    const [data, setData] = React.useState({
        brand_title: '', brand_subtitle: '', section_heading: '',
        hero_headline: '', hero_caption_top: '', hero_caption_main: '',
        footer_text: '',
        secondary_link_label: '', secondary_link_text: '',
    });
    const [heroUrl, setHeroUrl] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const fileRef = React.useRef(null);
    // Bust the image cache after upload/delete so the preview reloads.
    const [previewVersion, setPreviewVersion] = React.useState(0);

    const load = React.useCallback(async () => {
        try {
            const res = await loginPageApi.get();
            const cfg = res.data || {};
            // Mongo-Felder + Defaults trennen
            setData({
                brand_title: cfg.brand_title ?? '',
                brand_subtitle: cfg.brand_subtitle ?? '',
                section_heading: cfg.section_heading ?? '',
                hero_headline: cfg.hero_headline ?? '',
                hero_caption_top: cfg.hero_caption_top ?? '',
                hero_caption_main: cfg.hero_caption_main ?? '',
                footer_text: cfg.footer_text ?? '',
                secondary_link_label: cfg.secondary_link_label ?? '',
                secondary_link_text: cfg.secondary_link_text ?? '',
            });
            setHeroUrl(cfg.hero_image_url || null);
        } catch (e) {
            toast.error('Login-Seiten-Einstellungen konnten nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            await loginPageApi.save(data);
            toast.success('Login-Seite gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const validExt = ['.png', '.jpg', '.jpeg', '.svg', '.webp'];
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!validExt.includes(ext)) {
            toast.error(`Ungültiges Format. Erlaubt: ${validExt.join(', ')}`);
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Datei zu groß (max. 10 MB). Tipp: Backend skaliert automatisch auf 1600px.');
            return;
        }
        setUploading(true);
        try {
            await loginPageApi.uploadHero(file);
            toast.success('Hintergrundbild hochgeladen');
            setPreviewVersion((v) => v + 1);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Hintergrundbild wirklich entfernen? Das Default-Bild wird wieder angezeigt.')) return;
        try {
            await loginPageApi.deleteHero();
            toast.success('Bild entfernt');
            setPreviewVersion((v) => v + 1);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
        }
    };

    if (loading) {
        return <Card><CardContent className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>;
    }

    const heroPreview = heroUrl ? `${BACKEND_URL}${heroUrl}?v=${previewVersion}` : null;
    const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

    return (
        <div className="space-y-6" data-testid="login-page-settings">
            {/* Hero-Bild */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <Image className="h-5 w-5" />
                        Hintergrundbild der Login-Seite
                    </CardTitle>
                    <CardDescription>
                        Wird auf großen Bildschirmen rechts neben dem Anmelde-Formular eingeblendet.
                        Empfohlen: hochwertiges Foto, mindestens 1200&nbsp;px breit. Das Bild wird
                        automatisch auf max. 1600&nbsp;px Kantenlänge skaliert und als JPEG optimiert.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="rounded-lg overflow-hidden border bg-gray-100 aspect-[16/9] max-w-2xl">
                        {heroPreview ? (
                            <div
                                className="w-full h-full"
                                style={{ backgroundImage: `url("${heroPreview}")`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                                data-testid="login-hero-preview"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-sm text-gray-500" data-testid="login-hero-placeholder">
                                Kein eigenes Bild hochgeladen — Standard-Hintergrund wird angezeigt.
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            onChange={handleUpload}
                            className="hidden"
                            data-testid="login-hero-file-input"
                        />
                        <Button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            data-testid="login-hero-upload-btn"
                        >
                            {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade hoch…</> : (heroUrl ? 'Bild ersetzen' : 'Bild hochladen')}
                        </Button>
                        {heroUrl && (
                            <Button type="button" variant="outline" onClick={handleDelete} data-testid="login-hero-delete-btn">
                                Bild entfernen
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Texte */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <Pencil className="h-5 w-5" />
                        Texte der Login-Seite
                    </CardTitle>
                    <CardDescription>
                        Alle Texte können hier angepasst werden. Im Titel wird der Teil nach dem ersten
                        &bdquo;-&ldquo; automatisch farblich hervorgehoben (z. B. &bdquo;BGPR<span className="text-primary">-Akademie</span>&ldquo;).
                        Im Footer steht <code className="bg-gray-100 px-1 rounded text-[11px]">{`{year}`}</code> für das
                        aktuelle Jahr.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <LpField
                            label="Logo-Titel (links oben)"
                            value={data.brand_title}
                            onChange={(v) => update('brand_title', v)}
                            placeholder="BGPR-Akademie"
                            testid="lp-brand-title"
                        />
                        <LpField
                            label="Untertitel (links oben, klein)"
                            value={data.brand_subtitle}
                            onChange={(v) => update('brand_subtitle', v)}
                            placeholder="BGPR-Akademie"
                            testid="lp-brand-subtitle"
                        />
                    </div>

                    <LpField
                        label="Überschrift über dem Formular"
                        desc={'Wird mit rotem Unterstrich angezeigt (z. B. „Anmelden“)'}
                        value={data.section_heading}
                        onChange={(v) => update('section_heading', v)}
                        placeholder="Anmelden"
                        testid="lp-section-heading"
                    />

                    <div className="pt-4 border-t">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Rechte Hero-Spalte</h4>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <LpField
                                label="Große Überschrift (Hero)"
                                desc={'Teil nach dem ersten „-“ wird farblich hervorgehoben'}
                                value={data.hero_headline}
                                onChange={(v) => update('hero_headline', v)}
                                placeholder="BGPR-Akademie"
                                testid="lp-hero-headline"
                            />
                            <LpField
                                label="Kleine Überschrift unter der Hero-Überschrift"
                                value={data.hero_caption_top}
                                onChange={(v) => update('hero_caption_top', v)}
                                placeholder="BGPR-Akademie"
                                testid="lp-hero-caption-top"
                            />
                        </div>
                        <div className="mt-4">
                            <LpField
                                label="Untertitel-Absatz (Hero)"
                                value={data.hero_caption_main}
                                onChange={(v) => update('hero_caption_main', v)}
                                placeholder="Berliner Gesellschaft für …"
                                multiline
                                testid="lp-hero-caption-main"
                            />
                        </div>
                    </div>

                    <div className="pt-4 border-t">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Sekundärer Link unter dem Anmelde-Button</h4>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <LpField
                                label="Vor-Text"
                                value={data.secondary_link_label}
                                onChange={(v) => update('secondary_link_label', v)}
                                placeholder="Sie sind Kursteilnehmer/in?"
                                testid="lp-sec-label"
                            />
                            <LpField
                                label="Link-Text"
                                value={data.secondary_link_text}
                                onChange={(v) => update('secondary_link_text', v)}
                                placeholder="Zum Teilnehmer-Portal"
                                testid="lp-sec-text"
                            />
                        </div>
                    </div>

                    <div className="pt-4 border-t">
                        <LpField
                            label="Footer"
                            desc={`Verwende {year} als Platzhalter für das aktuelle Jahr`}
                            value={data.footer_text}
                            onChange={(v) => update('footer_text', v)}
                            placeholder="© {year} Berliner Gesellschaft …"
                            multiline
                            testid="lp-footer-text"
                        />
                    </div>

                    <div className="flex justify-end pt-2">
                        <Button onClick={save} disabled={saving} data-testid="login-page-save-btn">
                            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gespeichert…</> : 'Speichern'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}


function BankSettings() {
    const [cfg, setCfg] = React.useState({
        sender_name: '', sender_address: '', sender_city: '',
        sender_phone: '', sender_email: '', sender_web: '',
        sender_register: '', sender_tax_note: 'Gemäß § 4 Nr. 22a UStG umsatzsteuerbefreit.',
        account_holder: '', bank_name: '', iban: '', bic: '',
        payment_days: 14, footer_text: '',
    });
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => { load(); }, []);

    const load = async () => {
        try { const res = await settingsApi.getInvoiceSettings(); setCfg(prev => ({ ...prev, ...res.data })); }
        catch { /* defaults */ }
        finally { setLoading(false); }
    };

    const handleSave = async () => {
        setSaving(true);
        try { await settingsApi.saveInvoiceSettings(cfg); toast.success('Rechnungseinstellungen gespeichert'); }
        catch { toast.error('Fehler'); }
        finally { setSaving(false); }
    };

    const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

    if (loading) return <Card><CardContent className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>;

    return (
        <div className="space-y-6">
            {/* Absender */}
            <Card>
                <CardHeader><CardTitle className="text-base">Rechnungsabsender</CardTitle><CardDescription>Diese Daten erscheinen oben auf jeder Rechnung.</CardDescription></CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 max-w-2xl">
                    <div className="space-y-2 col-span-2"><Label>Vereins-/Firmenname *</Label><Input value={cfg.sender_name} onChange={e => set('sender_name', e.target.value)} placeholder="BGPR e.V." data-testid="inv-sender-name" /></div>
                    <div className="space-y-2"><Label>Straße + Nr.</Label><Input value={cfg.sender_address} onChange={e => set('sender_address', e.target.value)} placeholder="Musterstraße 1" /></div>
                    <div className="space-y-2"><Label>PLZ + Ort</Label><Input value={cfg.sender_city} onChange={e => set('sender_city', e.target.value)} placeholder="10115 Berlin" /></div>
                    <div className="space-y-2"><Label>Telefon</Label><Input value={cfg.sender_phone} onChange={e => set('sender_phone', e.target.value)} placeholder="030 12345678" /></div>
                    <div className="space-y-2"><Label>E-Mail</Label><Input value={cfg.sender_email} onChange={e => set('sender_email', e.target.value)} placeholder="info@bgpr.de" /></div>
                    <div className="space-y-2"><Label>Website</Label><Input value={cfg.sender_web} onChange={e => set('sender_web', e.target.value)} placeholder="www.bgpr.de" /></div>
                    <div className="space-y-2"><Label>Vereinsregister / Amtsgericht</Label><Input value={cfg.sender_register} onChange={e => set('sender_register', e.target.value)} placeholder="VR 12345, AG Charlottenburg" /></div>
                </CardContent>
            </Card>

            {/* Bankverbindung */}
            <Card>
                <CardHeader><CardTitle className="text-base">Bankverbindung</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 max-w-2xl">
                    <div className="space-y-2"><Label>Kontoinhaber</Label><Input value={cfg.account_holder} onChange={e => set('account_holder', e.target.value)} placeholder="BGPR e.V." data-testid="bank-account-holder" /></div>
                    <div className="space-y-2"><Label>Bank</Label><Input value={cfg.bank_name} onChange={e => set('bank_name', e.target.value)} placeholder="Berliner Sparkasse" data-testid="bank-name" /></div>
                    <div className="space-y-2"><Label>IBAN</Label><Input value={cfg.iban} onChange={e => set('iban', e.target.value)} placeholder="DE12 3456 7890 1234 5678 90" className="font-mono" data-testid="bank-iban" /></div>
                    <div className="space-y-2"><Label>BIC</Label><Input value={cfg.bic} onChange={e => set('bic', e.target.value)} placeholder="BELADEBEXXX" className="font-mono" data-testid="bank-bic" /></div>
                </CardContent>
            </Card>

            {/* Zahlungsbedingungen */}
            <Card>
                <CardHeader><CardTitle className="text-base">Zahlungsbedingungen & Steuern</CardTitle></CardHeader>
                <CardContent className="space-y-4 max-w-2xl">
                    <div className="space-y-2"><Label>Zahlungsziel (Tage)</Label><Input type="number" min="0" value={cfg.payment_days} onChange={e => set('payment_days', parseInt(e.target.value) || 0)} data-testid="inv-payment-days" /><p className="text-xs text-muted-foreground">0 = sofort fällig</p></div>
                    <div className="space-y-2"><Label>Steuerhinweis</Label><Input value={cfg.sender_tax_note} onChange={e => set('sender_tax_note', e.target.value)} placeholder="Gemäß § 4 Nr. 22a UStG umsatzsteuerbefreit." data-testid="inv-tax-note" /></div>
                    <div className="space-y-2"><Label>Fußzeile (optional)</Label><Textarea value={cfg.footer_text} onChange={e => set('footer_text', e.target.value)} placeholder="Vielen Dank für Ihre Anmeldung..." rows={2} /></div>
                </CardContent>
            </Card>

            <Button onClick={handleSave} disabled={saving} data-testid="save-invoice-settings">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Rechnungseinstellungen speichern
            </Button>
        </div>
    );
}

function PrerequisitesSettings() {
    const [data, setData] = React.useState({ options: [], notice: 'Im Einzelfall können Nachweise angefordert werden.' });
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [newOption, setNewOption] = React.useState('');

    React.useEffect(() => { load(); }, []);

    const load = async () => {
        try { const res = await settingsApi.getPrerequisites(); setData(res.data || { options: [], notice: '' }); }
        catch { /* defaults */ }
        finally { setLoading(false); }
    };

    const handleSave = async () => {
        setSaving(true);
        try { await settingsApi.savePrerequisites(data); toast.success('Vorkenntnisse gespeichert'); }
        catch { toast.error('Fehler'); }
        finally { setSaving(false); }
    };

    const addOption = () => {
        if (!newOption.trim()) return;
        setData(prev => ({ ...prev, options: [...prev.options, newOption.trim()] }));
        setNewOption('');
    };

    const removeOption = (idx) => {
        setData(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
    };

    if (loading) return <Card><CardContent className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>;

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <GraduationCap className="h-5 w-5" />
                        Vorkenntnisse für verkürzte Ausbildung
                    </CardTitle>
                    <CardDescription>
                        Diese Optionen werden als Pflicht-Checkboxen bei der Anmeldung zur verkürzten Herzgruppenleiter-Ausbildung angezeigt. Mindestens eine Option muss bestätigt werden.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 max-w-2xl">
                    {/* Existing options */}
                    {data.options.length > 0 && (
                        <div className="space-y-2">
                            {data.options.map((opt, idx) => (
                                <div key={idx} className="flex items-center gap-2 p-2.5 bg-muted/30 rounded-lg border" data-testid={`prereq-option-${idx}`}>
                                    <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                                    <span className="flex-1 text-sm">{opt}</span>
                                    <Button variant="ghost" size="sm" className="text-red-500 h-7 w-7 p-0" onClick={() => removeOption(idx)}>
                                        <XCircle className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add new */}
                    <div className="flex items-center gap-2">
                        <Input
                            value={newOption}
                            onChange={e => setNewOption(e.target.value)}
                            placeholder="z.B. Sportwissenschaftliches Studium"
                            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addOption())}
                            data-testid="prereq-new-input"
                        />
                        <Button variant="outline" size="sm" onClick={addOption} disabled={!newOption.trim()} data-testid="prereq-add">
                            <Plus className="h-4 w-4 mr-1" /> Hinzufügen
                        </Button>
                    </div>

                    {/* Notice text */}
                    <div className="space-y-2 pt-2">
                        <Label>Hinweistext (wird unter den Checkboxen angezeigt)</Label>
                        <Input
                            value={data.notice}
                            onChange={e => setData(prev => ({ ...prev, notice: e.target.value }))}
                            placeholder="Im Einzelfall können Nachweise angefordert werden."
                            data-testid="prereq-notice"
                        />
                    </div>

                    <Button onClick={handleSave} disabled={saving} data-testid="save-prerequisites">
                        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                        Vorkenntnisse speichern
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}


function PublicModulesSettings() {
    const [cfg, setCfg] = React.useState({ public_module_enabled: true });
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        settingsApi.getReminderSettings()
            .then(r => setCfg(prev => ({ ...prev, ...r.data })))
            .catch(() => { /* defaults */ })
            .finally(() => setLoading(false));
    }, []);

    const handleToggle = async (checked) => {
        setSaving(true);
        const newCfg = { ...cfg, public_module_enabled: checked };
        setCfg(newCfg);
        try {
            await settingsApi.saveReminderSettings(newCfg);
            toast.success(checked ? 'Modul aktiviert' : 'Modul deaktiviert');
        } catch {
            toast.error('Speichern fehlgeschlagen');
            setCfg(prev => ({ ...prev, public_module_enabled: !checked })); // rollback
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Card><CardContent className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="font-heading">Öffentliche Module</CardTitle>
                <CardDescription>Ein- oder ausschalten einzelner Funktionsbereiche auf der öffentlichen Website — z.B. bei technischen Problemen oder Wartung.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border">
                    <input
                        type="checkbox"
                        id="public-license-renewal"
                        checked={cfg.public_module_enabled !== false}
                        onChange={e => handleToggle(e.target.checked)}
                        disabled={saving}
                        className="w-4 h-4 mt-0.5 accent-primary"
                        data-testid="public-module-toggle"
                    />
                    <div className="flex-1">
                        <Label htmlFor="public-license-renewal" className="cursor-pointer font-medium">
                            Lizenzverlängerung für Übungsleiter anzeigen
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                            Zeigt den Block „Lizenzverlängerung für Übungsleiter" auf der öffentlichen Seminarseite.
                            Deaktivieren, um das Modul bei technischen Problemen temporär auszublenden.
                        </p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${cfg.public_module_enabled !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {cfg.public_module_enabled !== false ? 'Aktiv' : 'Deaktiviert'}
                    </span>
                </div>
            </CardContent>
        </Card>
    );
}


function ReminderSettings() {
    const [cfg, setCfg] = React.useState({ reminder_days: [90, 30], email_subject: 'Erinnerung: Ihre Herzgruppenleiter-Lizenz läuft bald ab', enabled: true, auto_scheduler_enabled: false, schedule_hour: 7, public_module_enabled: true });
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [newDay, setNewDay] = React.useState('');

    React.useEffect(() => { load(); }, []);

    const load = async () => {
        try { const r = await settingsApi.getReminderSettings(); setCfg(prev => ({ ...prev, ...r.data })); }
        catch { /* defaults */ }
        finally { setLoading(false); }
    };

    const handleSave = async () => {
        setSaving(true);
        try { await settingsApi.saveReminderSettings(cfg); toast.success('Erinnerungseinstellungen gespeichert'); }
        catch { toast.error('Fehler'); }
        finally { setSaving(false); }
    };

    const handleSend = async () => {
        setSending(true);
        try { const r = await settingsApi.triggerReminders(); toast.success(r.data.message); }
        catch { toast.error('Fehler beim Versenden'); }
        finally { setSending(false); }
    };

    const addDay = () => {
        const d = parseInt(newDay);
        if (!d || d < 1) return;
        if (cfg.reminder_days.includes(d)) { toast.error('Bereits vorhanden'); return; }
        setCfg(prev => ({ ...prev, reminder_days: [...prev.reminder_days, d].sort((a, b) => b - a) }));
        setNewDay('');
    };

    const removeDay = (d) => setCfg(prev => ({ ...prev, reminder_days: prev.reminder_days.filter(x => x !== d) }));

    if (loading) return <Card><CardContent className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>;

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Lizenz-Ablauf Erinnerungen
                    </CardTitle>
                    <CardDescription>Automatische E-Mail-Erinnerungen an Herzgruppenleiter, wenn ihre Lizenz bald abläuft.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 max-w-lg">
                    <div className="flex items-center gap-2">
                        <input type="checkbox" checked={cfg.enabled} onChange={e => setCfg(prev => ({ ...prev, enabled: e.target.checked }))} className="w-4 h-4 accent-primary" />
                        <Label>Erinnerungen aktiviert</Label>
                    </div>

                    <div className="space-y-2">
                        <Label>Erinnerung senden X Tage vor Ablauf</Label>
                        <div className="flex flex-wrap gap-2">
                            {cfg.reminder_days.map(d => (
                                <span key={d} className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                                    {d} Tage
                                    <button onClick={() => removeDay(d)} className="hover:text-red-500 ml-1"><XCircle className="h-3.5 w-3.5" /></button>
                                </span>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                            <Input type="number" min="1" value={newDay} onChange={e => setNewDay(e.target.value)} placeholder="z.B. 60" className="w-24" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDay())} />
                            <Button variant="outline" size="sm" onClick={addDay} disabled={!newDay}>Hinzufügen</Button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>E-Mail Betreff</Label>
                        <Input value={cfg.email_subject} onChange={e => setCfg(prev => ({ ...prev, email_subject: e.target.value }))} />
                        <p className="text-xs text-muted-foreground">Hinweis: Der Betreff wird beim manuellen Auslösen verwendet. Der eigentliche Mail-Text kommt aus der unten verlinkten Vorlage „Lizenz-Ablauf-Frühwarnung".</p>
                    </div>

                    {/* Verknüpfung zur Mail-Vorlage – beantwortet die häufige Rückfrage,
                        welche Vorlage tatsächlich versendet wird, und springt mit Deep-Link direkt
                        in den Editor (?type=license_expiry_reminder). */}
                    <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4 space-y-2" data-testid="reminder-template-link">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="space-y-1">
                                <p className="text-sm font-semibold text-rose-900 flex items-center gap-2">
                                    <FileText className="h-4 w-4" />
                                    Verknüpfte E-Mail-Vorlage
                                </p>
                                <p className="text-xs text-rose-900/80">
                                    Versandt wird die System-Vorlage <strong>„Lizenz-Ablauf-Frühwarnung"</strong>
                                    {' '}(<code className="text-[11px] bg-white/70 px-1 py-0.5 rounded">license_expiry_reminder</code>).
                                    Inhalt, Variablen ({'{{verfuegbare_ue}}'}, {'{{fehlende_ue}}'}, {'{{schwelle_label}}'} u. a.) und der Button-Link sind dort frei anpassbar.
                                </p>
                            </div>
                            <Button asChild variant="default" size="sm" className="bg-rose-600 hover:bg-rose-700">
                                <RouterLink
                                    to="/admin/settings/email-templates?type=license_expiry_reminder"
                                    data-testid="open-license-expiry-template-btn"
                                >
                                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                                    Vorlage bearbeiten
                                </RouterLink>
                            </Button>
                        </div>
                    </div>

                    <div className="border-t pt-4 space-y-3">
                        <h3 className="text-sm font-semibold">Automatischer Versand (Scheduler)</h3>
                        <div className="flex items-center gap-2">
                            <input type="checkbox" id="auto-scheduler" checked={cfg.auto_scheduler_enabled} onChange={e => setCfg(prev => ({ ...prev, auto_scheduler_enabled: e.target.checked }))} className="w-4 h-4 accent-primary" data-testid="auto-scheduler-toggle" />
                            <Label htmlFor="auto-scheduler" className="cursor-pointer">Automatischer täglicher Versand aktiviert</Label>
                        </div>
                        {cfg.auto_scheduler_enabled && (
                            <div className="flex items-center gap-2 pl-6">
                                <Label className="text-sm">Uhrzeit (UTC):</Label>
                                <Input type="number" min="0" max="23" value={cfg.schedule_hour} onChange={e => setCfg(prev => ({ ...prev, schedule_hour: parseInt(e.target.value) || 0 }))} className="w-20" />
                                <span className="text-xs text-muted-foreground">Uhr</span>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">Wenn deaktiviert, werden keine automatischen Erinnerungen versendet — nur manuell über den Button unten.</p>
                    </div>

                    <div className="flex gap-2 pt-2">
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                            Speichern
                        </Button>
                        <Button variant="outline" onClick={handleSend} disabled={sending}>
                            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
                            Jetzt Erinnerungen versenden
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function PerformanceStatsCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true); else setRefreshing(true);
        try {
            const res = await systemApi.performanceStats();
            setData(res.data);
        } catch {
            toast.error('Performance-Daten konnten nicht geladen werden');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const formatUptime = (sec) => {
        if (!sec || sec < 0) return '–';
        const d = Math.floor(sec / 86400);
        const h = Math.floor((sec % 86400) / 3600);
        const m = Math.floor((sec % 3600) / 60);
        if (d > 0) return `${d}d ${h}h ${m}m`;
        if (h > 0) return `${h}h ${m}m`;
        return `${m}m`;
    };

    const tone = (pct) => {
        if (pct == null) return 'text-gray-500';
        if (pct >= 85) return 'text-red-600';
        if (pct >= 70) return 'text-amber-600';
        return 'text-emerald-600';
    };

    const fmtKb = (kb) => {
        if (kb == null) return '–';
        if (kb >= 1024) return `${(kb / 1024).toFixed(2)} MB`;
        return `${kb.toFixed(1)} KB`;
    };

    if (loading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <Activity className="h-5 w-5" />
                        Performance & Ressourcen
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="py-8 flex justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    const sys = data?.system || {};
    const dbS = data?.database || {};
    const top = data?.top_collections || [];
    const totalIndexes = (data?.collections || []).reduce((sum, c) => sum + (c.indexes || 0), 0);
    const collectionsWithoutCustomIdx = (data?.collections || []).filter(c => (c.indexes || 0) <= 1).length;

    return (
        <Card data-testid="performance-stats-card">
            <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Activity className="h-5 w-5" />
                            Performance & Ressourcen
                        </CardTitle>
                        <CardDescription>
                            Live-Snapshot aus Backend &amp; MongoDB. Hilft, Engpässe und ungenutzte Indizes früh zu erkennen.
                        </CardDescription>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => load(true)}
                        disabled={refreshing}
                        data-testid="performance-refresh-btn"
                    >
                        {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                        Aktualisieren
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                {/* KPI-Reihe */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="border rounded-lg p-3 bg-card">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                            <Cpu className="h-3.5 w-3.5" /> CPU
                        </div>
                        <div className={`text-2xl font-semibold ${tone(sys.cpu_percent)}`} data-testid="perf-cpu">
                            {sys.cpu_percent != null ? `${sys.cpu_percent.toFixed(1)}%` : '–'}
                        </div>
                    </div>
                    <div className="border rounded-lg p-3 bg-card">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                            <MemoryStick className="h-3.5 w-3.5" /> Memory
                        </div>
                        <div className={`text-2xl font-semibold ${tone(sys.memory_percent)}`} data-testid="perf-memory">
                            {sys.memory_percent != null ? `${sys.memory_percent.toFixed(1)}%` : '–'}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            {sys.memory_used_mb != null && sys.memory_total_mb != null
                                ? `${(sys.memory_used_mb/1024).toFixed(1)} / ${(sys.memory_total_mb/1024).toFixed(1)} GB`
                                : ''}
                        </div>
                    </div>
                    <div className="border rounded-lg p-3 bg-card">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                            <HardDrive className="h-3.5 w-3.5" /> Disk
                        </div>
                        <div className={`text-2xl font-semibold ${tone(sys.disk_percent)}`} data-testid="perf-disk">
                            {sys.disk_percent != null ? `${sys.disk_percent.toFixed(1)}%` : '–'}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            {sys.disk_free_gb != null ? `${sys.disk_free_gb.toFixed(1)} GB frei` : ''}
                        </div>
                    </div>
                    <div className="border rounded-lg p-3 bg-card">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                            <Clock className="h-3.5 w-3.5" /> Backend-Uptime
                        </div>
                        <div className="text-2xl font-semibold text-foreground" data-testid="perf-uptime">
                            {formatUptime(data?.uptime_seconds)}
                        </div>
                    </div>
                </div>

                {/* DB-Aggregat */}
                <div className="border rounded-lg p-4 bg-muted/30">
                    <div className="flex items-center gap-2 text-sm font-medium mb-3">
                        <Database className="h-4 w-4" />
                        Datenbank-Übersicht
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <div>
                            <div className="text-xs text-muted-foreground">Daten</div>
                            <div className="font-semibold">{dbS.data_size_mb != null ? `${dbS.data_size_mb} MB` : '–'}</div>
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground">Indizes</div>
                            <div className="font-semibold">{dbS.index_size_mb != null ? `${dbS.index_size_mb} MB` : '–'}</div>
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground">Storage</div>
                            <div className="font-semibold">{dbS.storage_size_mb != null ? `${dbS.storage_size_mb} MB` : '–'}</div>
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground">Collections</div>
                            <div className="font-semibold">{dbS.collections ?? '–'}</div>
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground">Dokumente gesamt</div>
                            <div className="font-semibold">{(dbS.objects ?? 0).toLocaleString('de-DE')}</div>
                        </div>
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground">
                        Insgesamt {totalIndexes} Index{totalIndexes === 1 ? '' : 'es'} ·{' '}
                        {collectionsWithoutCustomIdx > 0
                            ? <span className="text-amber-700">{collectionsWithoutCustomIdx} Collection{collectionsWithoutCustomIdx === 1 ? '' : 's'} nur mit Standard-`_id_`-Index (potenzielles Tuning-Feld bei Wachstum).</span>
                            : 'Alle Collections haben mind. einen zusätzlichen Index.'}
                    </div>
                </div>

                {/* Top-5 Collections */}
                {top.length > 0 && (
                    <div>
                        <div className="text-sm font-medium mb-2 flex items-center gap-2">
                            <Database className="h-4 w-4" />
                            Top 5 Collections (nach Datengröße)
                        </div>
                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm" data-testid="perf-top-collections">
                                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-3 py-2">Collection</th>
                                        <th className="text-right px-3 py-2">Dokumente</th>
                                        <th className="text-right px-3 py-2">Daten</th>
                                        <th className="text-right px-3 py-2">Indizes</th>
                                        <th className="text-right px-3 py-2">Anzahl Idx</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {top.map(c => (
                                        <tr key={c.name} className="border-t">
                                            <td className="px-3 py-2 font-mono text-xs">{c.name}</td>
                                            <td className="px-3 py-2 text-right">{c.documents.toLocaleString('de-DE')}</td>
                                            <td className="px-3 py-2 text-right">{fmtKb(c.data_size_kb)}</td>
                                            <td className="px-3 py-2 text-right">{fmtKb(c.index_size_kb)}</td>
                                            <td className="px-3 py-2 text-right">{c.indexes}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <p className="text-[11px] text-muted-foreground">
                    Snapshot vom {data?.generated_at ? new Date(data.generated_at).toLocaleString('de-DE') : '–'} · read-only · ohne Auswirkung auf laufende Prozesse.
                </p>
            </CardContent>
        </Card>
    );
}


function SessionSettingsCard() {
    const [minutes, setMinutes] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Live preview: track idle time since the last mouse/keyboard event
    const [idleSeconds, setIdleSeconds] = useState(0);    useEffect(() => {
        settingsApi.getSession()
            .then((r) => setMinutes(String(r.data.idle_timeout_minutes || 0)))
            .catch(() => setMinutes('0'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        let lastActivity = Date.now();
        const reset = () => { lastActivity = Date.now(); };
        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        const id = setInterval(() => {
            setIdleSeconds(Math.floor((Date.now() - lastActivity) / 1000));
        }, 1000);
        return () => {
            clearInterval(id);
            events.forEach((e) => window.removeEventListener(e, reset));
        };
    }, []);

    const fmt = (s) => {
        const neg = s < 0;
        const abs = Math.abs(s);
        const mm = Math.floor(abs / 60);
        const ss = abs % 60;
        return `${neg ? '-' : ''}${mm}:${ss.toString().padStart(2, '0')}`;
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const parsed = parseInt(minutes, 10);
            if (Number.isNaN(parsed) || parsed < 0 || parsed > 1440) {
                toast.error('Bitte einen Wert zwischen 0 und 1440 Minuten eingeben.');
                setSaving(false);
                return;
            }
            await settingsApi.saveSession({ idle_timeout_minutes: parsed });
            toast.success('Session-Einstellungen gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
        } finally { setSaving(false); }
    };

    const parsedMin = parseInt(minutes, 10);
    const previewEnabled = !Number.isNaN(parsedMin) && parsedMin > 0;
    const totalSeconds = previewEnabled ? parsedMin * 60 : 0;
    const remaining = previewEnabled ? totalSeconds - idleSeconds : 0;
    const warningActive = previewEnabled && remaining <= 60;

    return (
        <Card data-testid="session-settings-card">
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Automatischer Logout (Session-Timeout)
                </CardTitle>
                <CardDescription>
                    Nach wie vielen Minuten Inaktivität sollen Teilnehmer und Admins automatisch abgemeldet werden?
                    Gilt für alle Benutzer. 0 = deaktiviert.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    <div className="flex items-end gap-3 max-w-md">
                        <div className="flex-1 space-y-1">
                            <Label htmlFor="idle_timeout" className="text-sm">Inaktivität in Minuten</Label>
                            <Input
                                id="idle_timeout"
                                type="number" min="0" max="1440"
                                value={minutes}
                                onChange={(e) => setMinutes(e.target.value)}
                                placeholder="30"
                                disabled={loading}
                                data-testid="session-timeout-input"
                            />
                            <p className="text-xs text-muted-foreground">Empfohlen: 30-60 Minuten. Maximal 1440 (24h).</p>
                        </div>
                        <Button onClick={handleSave} disabled={saving || loading} data-testid="save-session-settings">
                            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                            Speichern
                        </Button>
                    </div>

                    {/* Live-Vorschau */}
                    {previewEnabled && (
                        <div className="rounded-lg border bg-gray-50 p-3 space-y-1" data-testid="session-preview-box">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Live-Vorschau</div>
                            <div className="grid sm:grid-cols-2 gap-2 text-sm">
                                <div>
                                    <span className="text-gray-600">Inaktiv seit: </span>
                                    <span className="font-mono font-semibold tabular-nums" data-testid="preview-idle">{fmt(idleSeconds)} Min</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Session läuft ab in: </span>
                                    <span
                                        className={`font-mono font-semibold tabular-nums ${warningActive ? 'text-amber-600' : remaining <= 0 ? 'text-red-600' : 'text-green-600'}`}
                                        data-testid="preview-remaining"
                                    >
                                        {remaining > 0 ? fmt(remaining) : (remaining === 0 ? '0:00' : 'abgelaufen')}
                                    </span>
                                </div>
                            </div>
                            <p className="text-xs text-gray-400">
                                Die Vorschau bezieht sich nur auf diesen Tab. Bewegen Sie Maus oder Tastatur, um den Timer zurückzusetzen.
                                60 Sekunden vor Ablauf erscheint für alle Benutzer ein Dialog mit Countdown.
                            </p>
                        </div>
                    )}
                    {!previewEnabled && !loading && (
                        <div className="rounded-lg border bg-gray-50 p-3 text-xs text-gray-500" data-testid="session-preview-disabled">
                            Auto-Logout ist aktuell deaktiviert (0 Minuten). Tragen Sie eine Zahl &gt; 0 ein, um die Live-Vorschau zu sehen.
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}



function MaintenanceModeCard() {
    const [enabled, setEnabled] = useState(false);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        settingsApi.getMaintenance()
            .then((r) => {
                setEnabled(!!r.data.enabled);
                setTitle(r.data.title || '');
                setMessage(r.data.message || '');
            })
            .catch(() => { /* fallback bleibt leer */ })
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await settingsApi.saveMaintenance({ enabled, title: title.trim(), message: message.trim() });
            toast.success(enabled ? 'Wartungsmodus aktiviert – die öffentliche Seite ist für Mitglieder nicht mehr buchbar.' : 'Wartungsmodus deaktiviert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card data-testid="maintenance-mode-card">
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <Wrench className="h-5 w-5" />
                    Wartungsmodus für die öffentliche Seite
                </CardTitle>
                <CardDescription>
                    Schaltet die Seminar-Übersicht und den Buchungsflow für Interessenten ab.
                    Bestehende Mitglieder-Logins und der Admin-Bereich bleiben weiterhin erreichbar,
                    damit Sie auch im Wartungsfenster handlungsfähig sind.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                    <Switch
                        checked={enabled}
                        onCheckedChange={setEnabled}
                        disabled={loading}
                        data-testid="maintenance-mode-toggle"
                    />
                    <span className="text-sm">
                        {enabled ? (
                            <span className="font-semibold text-amber-700">Öffentliche Seite ist aktuell offline</span>
                        ) : (
                            <span>Öffentliche Seite ist online</span>
                        )}
                    </span>
                </label>

                <div className="space-y-2">
                    <Label htmlFor="maintenance-title" className="text-sm">Überschrift auf der Wartungsseite</Label>
                    <Input
                        id="maintenance-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="z.B. Kurze Wartungspause"
                        disabled={loading}
                        data-testid="maintenance-title-input"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="maintenance-message" className="text-sm">Hinweis-Text</Label>
                    <textarea
                        id="maintenance-message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={3}
                        placeholder="Wir sind in Kürze wieder für Sie da…"
                        disabled={loading}
                        className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        data-testid="maintenance-message-input"
                    />
                </div>

                <div className="flex justify-end pt-2">
                    <Button onClick={handleSave} disabled={saving || loading} data-testid="save-maintenance-settings">
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Speichern
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}


function SeminarCapacityCard() {
    const [threshold, setThreshold] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        settingsApi.getSeminarCapacity()
            .then((r) => setThreshold(String(r.data.low_seats_threshold ?? 3)))
            .catch(() => setThreshold('3'))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        const parsed = parseInt(threshold, 10);
        if (Number.isNaN(parsed) || parsed < 0 || parsed > 999) {
            toast.error('Bitte einen Wert zwischen 0 und 999 eingeben.');
            return;
        }
        setSaving(true);
        try {
            await settingsApi.saveSeminarCapacity({ low_seats_threshold: parsed });
            toast.success('Kapazitäts-Einstellungen gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
        } finally { setSaving(false); }
    };

    return (
        <Card data-testid="seminar-capacity-settings-card">
            <CardHeader>
                <CardTitle className="font-heading">Seminar-Ampelsystem</CardTitle>
                <CardDescription>
                    Ab wie vielen freien Plätzen soll auf der öffentlichen Seminar-Liste die gelbe Ampel
                    („Nur noch X Plätze") angezeigt werden? Der Wert gilt seminarübergreifend.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="flex items-end gap-3 max-w-md">
                    <div className="flex-1 space-y-1">
                        <Label htmlFor="low_seats_threshold" className="text-sm">Schwellwert (freie Plätze)</Label>
                        <Input
                            id="low_seats_threshold"
                            type="number" min="0" max="999"
                            value={threshold}
                            onChange={(e) => setThreshold(e.target.value)}
                            placeholder="3"
                            disabled={loading}
                            data-testid="seminar-capacity-threshold-input"
                        />
                        <p className="text-xs text-muted-foreground">
                            Beispiel: 3 → Sobald ≤ 3 Plätze frei sind, erscheint die gelbe Warnung.
                            Vorher wird Grün („Findet statt") angezeigt, sobald die Mindestteilnehmerzahl erreicht ist.
                        </p>
                    </div>
                    <Button onClick={handleSave} disabled={saving || loading} data-testid="save-seminar-capacity-settings">
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Speichern
                    </Button>
                </div>

                <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-slate-400" />
                        <div>
                            <div className="font-semibold text-slate-700">Anmeldung möglich</div>
                            <div className="text-slate-500">Mindestzahl noch nicht erreicht</div>
                        </div>
                    </div>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        <div>
                            <div className="font-semibold text-emerald-700">Findet statt</div>
                            <div className="text-emerald-600">Mindestzahl erreicht</div>
                        </div>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-amber-500" />
                        <div>
                            <div className="font-semibold text-amber-700">Nur noch X Plätze</div>
                            <div className="text-amber-600">≤ Schwellwert</div>
                        </div>
                    </div>
                    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-rose-500" />
                        <div>
                            <div className="font-semibold text-rose-700">Ausgebucht – Warteliste</div>
                            <div className="text-rose-600">Max. erreicht</div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}


function NotificationSettings() {
    const [email, setEmail] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        let active = true;
        settingsApi.getNotifications()
            .then((r) => { if (active) setEmail(r.data.admin_notification_email || ''); })
            .catch(() => { /* empty default */ })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    const handleSave = async () => {
        const trimmed = email.trim();
        if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            toast.error('Bitte eine gültige E-Mail-Adresse eingeben.');
            return;
        }
        setSaving(true);
        try {
            await settingsApi.saveNotifications({ admin_notification_email: trimmed });
            toast.success('System-Benachrichtigungen gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card data-testid="notification-settings-card">
            <CardHeader>
                <CardTitle className="font-heading">System-Benachrichtigungen</CardTitle>
                <CardDescription>
                    An welche E-Mail-Adresse sollen automatische System-Nachrichten aus dem Admin-Bereich gesendet werden?
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
                <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4 text-sm text-blue-800">
                    <p className="font-medium mb-1">Was wird hier versendet?</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>Ein Akademie-Mitglied hat eine bestehende Lizenz zur Prüfung hochgeladen</li>
                        <li>Ein Akademie-Mitglied hat einen Lizenzverlängerungs-Antrag gestellt (geplant)</li>
                        <li>Weitere System-Hinweise, die die Geschäftsstelle erreichen sollen</li>
                    </ul>
                    <p className="mt-2 text-xs text-blue-600">
                        Wird hier nichts eingetragen, verwendet das System die Absender-Adresse aus dem SMTP-Zugang als Fallback.
                    </p>
                </div>

                <div className="space-y-2 max-w-lg">
                    <Label htmlFor="admin-notification-email">Geschäftsstelle E-Mail</Label>
                    <Input
                        id="admin-notification-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="info@bgpr.org"
                        disabled={loading}
                        data-testid="notifications-admin-email"
                    />
                </div>

                <div className="flex items-center justify-end pt-2">
                    <Button onClick={handleSave} disabled={saving || loading} data-testid="notifications-save-btn">
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Speichern
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}



